import { random } from "lodash";

import { MOCK_SITE_SAMPLE_IDS } from "./constants";

export const getPerformance = async (site, window, start, end) => {
  if (window == "day") {
    return getDayData(site, start);
  }
  if (window == "week" || window == "month") return getWindowData(window);
  return applyCosts(generateData(start, end, window));
};

const getDayData = async (site, start) => {
  const today = new Date();
  const todayString = `${today.getFullYear()}-${
    today.getMonth() + 1
  }-${today.getDate()}`;
  const isToday = todayString === start;

  if (site == MOCK_SITE_SAMPLE_IDS.optimisation_amber) {
    return await getEnergyProfile("optimisation_amber", isToday, "17:35:00");
  }

  if (site == MOCK_SITE_SAMPLE_IDS.optimisation_amber_solar_topup) {
    return await getEnergyProfile("optimisation_amber", isToday, "11:50:00");
  }

  if (site == MOCK_SITE_SAMPLE_IDS.optimisation_tou) {
    return await getEnergyProfile("optimisation_tou", isToday, "04:35:00");
  }

  if (site == MOCK_SITE_SAMPLE_IDS.optimisation_tou_solar_topup) {
    return await getEnergyProfile(
      "optimisation_tou_solar_topup",
      isToday,
      "13:05:00"
    );
  }

  if (site == MOCK_SITE_SAMPLE_IDS.optimisation_tou_ev) {
    return await getEnergyProfile("optimisation_tou_ev", isToday, "13:05:00");
  }

  return getWindowData("day");
};

export const getEnergyProfile = async (profile, isToday, mockTime) => {
  const response = await fetch(`/energy/${profile}.json`).then((r) => {
    return r.json();
  });
  // Map items in response and add additional key cost_cumulative using formula:
  // cost_cumulative = price_import * grid_import + price_export * grid_export
  let cost_cumulative = 0;
  const currentDate = new Date();
  const data = response.map((item) => {
    const currentDateString = currentDate.toISOString().split("T")[0];
    const updatedTimestamp = `${currentDateString}T${
      item.timestamp.split("T")[1]
    }`;
    // Assume grid_import is positive grid, grid_export is negative grid
    // convert power to 5min energy
    const grid_import = (item.grid_import ?? 0) / 12.0;
    const grid_export = (item.grid_export ?? 0) / 12.0;
    const price_import = item.price_import ?? 0;
    const price_export = item.price_export ?? 0;
    const cost_import = price_import * grid_import;
    const cost_export = price_export * grid_export;
    const cost_net = cost_import + cost_export;
    cost_cumulative += price_import * grid_import + price_export * grid_export;

    // If showing 'today', default records after mock 'now'
    const time = item.timestamp.split("T")[1];
    const timeOnly = time.split(/[+-]/)[0];
    return timeOnly > mockTime && isToday
      ? { timestamp: item.timestamp }
      : {
          ...item,
          cost_cumulative,
          cost_import,
          cost_export,
          cost_net,
          timestamp: isToday ? updatedTimestamp : item.timestamp,
        };
  });
  return data;
};

const generateData = (
  start,
  end = new Date().toISOString().split("T")[0],
  window = "day"
) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  endDate.setHours(23, 59, 59, 999);

  const intervals = {
    day: 5 * 60 * 1000,
    week: 30 * 60 * 1000,
    month: 24 * 60 * 60 * 1000,
    year: 30 * 24 * 60 * 60 * 1000,
  };

  const daily_factor = {
    day: 1,
    week: 1,
    month: 1,
    year: 30,
  };

  if (!intervals[window]) {
    throw new Error(
      "Invalid window type. Use 'day', 'week', 'month', or 'year'."
    );
  }

  const interval = intervals[window];
  const result = [];

  for (
    let time = startDate.getTime();
    time <= endDate.getTime();
    time += interval
  ) {
    result.push({
      battery: +(Math.random() * 2 - 1).toFixed(4),
      ev: +(Math.random() * 5 * daily_factor[window]).toFixed(4),
      grid: +(Math.random() * 15 * daily_factor[window]).toFixed(4),
      grid_import: +(Math.random() * 10 * daily_factor[window]).toFixed(4),
      grid_export: -(Math.random() * 5 * daily_factor[window]).toFixed(4),
      battery_import: +(Math.random() * 10 * daily_factor[window]).toFixed(4),
      battery_export: -(Math.random() * 10 * daily_factor[window]).toFixed(4),
      house: +(Math.random() * 10 * daily_factor[window]).toFixed(4),
      hotwater: +(Math.random() * 10 * daily_factor[window]).toFixed(4),
      soc: +(Math.random() * 100).toFixed(4),
      solar: +(Math.random() * 5 * daily_factor[window]).toFixed(4),
      timestamp: new Date(time).toISOString(),
    });
  }

  return result;
};

// If we want specific data then just replace return [] with data for each type

export const getWindowData = (window = "day") => {
  switch (window) {
    case "day":
      return getDefaultDayData().map((d) => {
        const currentDate = new Date();
        const currentDateString = currentDate.toISOString().split("T")[0];
        const updatedTimestamp = `${currentDateString}T${
          d.timestamp.split("T")[1]
        }`;
        const updatedDate = new Date(updatedTimestamp);
        var price_factor = 1.0;

        const hour = updatedDate.getHours();
        if (hour === 7 || hour === 8) {
          price_factor = 2.0;
        } else if (hour == 17) {
          price_factor = 40.0;
        } else if (hour === 13 || hour === 15) {
          price_factor = 0.5;
        } else if (hour >= 18 && hour <= 20) {
          price_factor = 6.0;
        }
        const price_import = 1.14234 * price_factor * random(true);
        const price_export = (price_import - 1.07612) * -2;

        const grid_import = d.grid_import
          ? d.grid_import
          : Math.abs(d.grid) / 12.0;
        const grid_export = d.grid_export
          ? d.grid_export
          : Math.abs(d.grid) / 12.0;
        const cost_import = price_import * grid_import;
        const cost_export = price_export * grid_export;
        const cost_net = cost_import + cost_export;

        if (updatedDate >= currentDate) {
          return {
            timestamp: updatedTimestamp,
          };
          // return {
          //   ...d,
          //   timestamp: updatedTimestamp,
          //   price_import,
          //   price_export,
          //   grid: null,
          //   battery: null,
          //   ev: null,
          // };
        }
        return {
          ...d,
          timestamp: updatedTimestamp,
          price_import,
          price_export,
          cost_import,
          cost_export,
          cost_net,
        };
      });
    case "week":
      return applyCosts(getWeekData());
    case "month":
      console.log("get month data");
      return applyCosts(getMonthData());
    case "year":
      console.log("get year data");
      return applyCosts(getYearData());
  }
};

const applyCosts = (data) => {
  return data.map((d) => {
    const grid_import = d.grid_import ? d.grid_import : Math.abs(d.grid);
    const grid_export = d.grid_export ? d.grid_export : Math.abs(d.grid) * -1;
    const cost_import = grid_import * 0.42;
    const cost_export = grid_export * 0.04;
    const cost_net = cost_import + cost_export;
    return {
      ...d,
      cost_import,
      cost_export,
      cost_net,
    };
  });
};

const getDefaultDayData = () => {
  return [
    {
      demand_window: true,
      battery: 0.0,
      ev: 0.0,
      hotwater: 0.0,
      grid: 0.4021266666666667,
      house: 0.4021266666666667,
      soc: 9.98917,
      solar: 0.0,
      timestamp: "2025-03-26T00:00:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.25575666666666663,
      house: 0.25575666666666663,
      soc: 9.98917,
      solar: 0.0,
      timestamp: "2025-03-26T00:05:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.28981999999999997,
      house: 0.28981999999999997,
      soc: 9.98917,
      solar: 0.0,
      timestamp: "2025-03-26T00:10:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.6871633333333333,
      house: 0.6871633333333333,
      soc: 9.98917,
      solar: 0.0,
      timestamp: "2025-03-26T00:15:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.37814000000000003,
      house: 0.37814000000000003,
      soc: 9.98917,
      solar: 0.0,
      timestamp: "2025-03-26T00:20:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.43965333333333334,
      house: 0.43965333333333334,
      soc: 9.98917,
      solar: 0.0,
      timestamp: "2025-03-26T00:25:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.28465666666666667,
      house: 0.28465666666666667,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T00:30:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.16112333333333334,
      house: 0.16112333333333334,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T00:35:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.16097666666666666,
      house: 0.16097666666666666,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T00:40:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.21468666666666666,
      house: 0.21468666666666666,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T00:45:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.30820666666666663,
      house: 0.30820666666666663,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T00:50:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.26289999999999997,
      house: 0.26289999999999997,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T00:55:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.27413333333333334,
      house: 0.27413333333333334,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T01:00:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.3164166666666667,
      house: 0.3164166666666667,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T01:05:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.3862833333333333,
      house: 0.3862833333333333,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T01:10:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.19728333333333337,
      house: 0.19728333333333337,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T01:15:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.16525666666666666,
      house: 0.16525666666666666,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T01:20:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.3765,
      house: 0.3765,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T01:25:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.25926333333333335,
      house: 0.25926333333333335,
      soc: 9.98915,
      solar: 0.0,
      timestamp: "2025-03-26T01:30:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.19347666666666669,
      house: 0.19347666666666669,
      soc: 9.98914,
      solar: 0.0,
      timestamp: "2025-03-26T01:35:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.23257666666666665,
      house: 0.23257666666666665,
      soc: 9.98914,
      solar: 0.0,
      timestamp: "2025-03-26T01:40:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.33108,
      house: 0.33108,
      soc: 9.98912,
      solar: 0.0,
      timestamp: "2025-03-26T01:45:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.25054333333333334,
      house: 0.25054333333333334,
      soc: 9.98912,
      solar: 0.0,
      timestamp: "2025-03-26T01:50:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.26768333333333333,
      house: 0.26768333333333333,
      soc: 9.98912,
      solar: 0.0,
      timestamp: "2025-03-26T01:55:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.32537666666666665,
      house: 0.32537666666666665,
      soc: 9.98912,
      solar: 0.0,
      timestamp: "2025-03-26T02:00:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.24682,
      house: 0.24682,
      soc: 9.98912,
      solar: 0.0,
      timestamp: "2025-03-26T02:05:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.24956333333333333,
      house: 0.24956333333333333,
      soc: 9.98912,
      solar: 0.0,
      timestamp: "2025-03-26T02:10:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.21873333333333334,
      house: 0.21873333333333334,
      soc: 9.98912,
      solar: 0.0,
      timestamp: "2025-03-26T02:15:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.22423666666666664,
      house: 0.22423666666666664,
      soc: 9.98912,
      solar: 0.0,
      timestamp: "2025-03-26T02:20:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.19540666666666667,
      house: 0.19540666666666667,
      soc: 9.98912,
      solar: 0.0,
      timestamp: "2025-03-26T02:25:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.29613,
      house: 0.29613,
      soc: 9.98912,
      solar: 0.0,
      timestamp: "2025-03-26T02:30:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.37344333333333335,
      house: 0.37344333333333335,
      soc: 9.98912,
      solar: 0.0,
      timestamp: "2025-03-26T02:35:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.21148333333333333,
      house: 0.21148333333333333,
      soc: 9.98911,
      solar: 0.0,
      timestamp: "2025-03-26T02:40:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.25257,
      house: 0.25257,
      soc: 9.98911,
      solar: 0.0,
      timestamp: "2025-03-26T02:45:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.24553333333333333,
      house: 0.24553333333333333,
      soc: 9.98911,
      solar: 0.0,
      timestamp: "2025-03-26T02:50:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.25377,
      house: 0.25377,
      soc: 9.98911,
      solar: 0.0,
      timestamp: "2025-03-26T02:55:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.1972066666666667,
      house: 0.1972066666666667,
      soc: 9.98911,
      solar: 0.0,
      timestamp: "2025-03-26T03:00:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.2511966666666667,
      house: 0.2511966666666667,
      soc: 9.98911,
      solar: 0.0,
      timestamp: "2025-03-26T03:05:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.36476,
      house: 0.36476,
      soc: 9.98911,
      solar: 0.0,
      timestamp: "2025-03-26T03:10:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.2529133333333333,
      house: 0.2529133333333333,
      soc: 9.98911,
      solar: 0.0,
      timestamp: "2025-03-26T03:15:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.2632,
      house: 0.2632,
      soc: 9.98909,
      solar: 0.0,
      timestamp: "2025-03-26T03:20:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.24753333333333333,
      house: 0.24753333333333333,
      soc: 9.98909,
      solar: 0.0,
      timestamp: "2025-03-26T03:25:00+11:00",
    },
    {
      battery: -4.0534300000000005,
      ev: 0.0,
      grid: 4.603666666666666,
      house: 0.5502366666666667,
      soc: 12.2458,
      solar: 0.0,
      timestamp: "2025-03-26T03:30:00+11:00",
    },
    {
      battery: -2.60536,
      ev: 0.0,
      grid: 2.8974966666666666,
      house: 0.29213666666666666,
      soc: 13.6898,
      solar: 0.0,
      timestamp: "2025-03-26T03:35:00+11:00",
    },
    {
      battery: -0.27095,
      ev: 0.0,
      grid: 0.4541533333333333,
      house: 0.18320333333333333,
      soc: 13.8442,
      solar: 0.0,
      timestamp: "2025-03-26T03:40:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.19857,
      house: 0.19857,
      soc: 13.8442,
      solar: 0.0,
      timestamp: "2025-03-26T03:45:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.33085333333333333,
      house: 0.33085333333333333,
      soc: 13.8442,
      solar: 0.0,
      timestamp: "2025-03-26T03:50:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.25174,
      house: 0.25174,
      soc: 13.8442,
      solar: 0.0,
      timestamp: "2025-03-26T03:55:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.30514,
      house: 0.30514,
      soc: 13.8442,
      solar: 0.0,
      timestamp: "2025-03-26T04:00:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.29760333333333333,
      house: 0.29760333333333333,
      soc: 13.8442,
      solar: 0.0,
      timestamp: "2025-03-26T04:05:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.30906666666666666,
      house: 0.30906666666666666,
      soc: 13.8442,
      solar: 0.0,
      timestamp: "2025-03-26T04:10:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.16103666666666666,
      house: 0.16103666666666666,
      soc: 13.8442,
      solar: 0.0,
      timestamp: "2025-03-26T04:15:00+11:00",
    },
    {
      battery: -1.8412866666666665,
      ev: 0.0,
      grid: 2.0692166666666667,
      house: 0.22793,
      soc: 14.8609,
      solar: 0.0,
      timestamp: "2025-03-26T04:20:00+11:00",
    },
    {
      battery: -4.255739999999999,
      ev: 0.0,
      grid: 4.626753333333333,
      house: 0.3710133333333333,
      soc: 17.1723,
      solar: 0.0,
      timestamp: "2025-03-26T04:25:00+11:00",
    },
    {
      battery: -1.1858833333333334,
      ev: 0.0,
      grid: 1.63759,
      house: 0.4517066666666667,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T04:30:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.28568666666666664,
      house: 0.28568666666666664,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T04:35:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.34795,
      house: 0.34795,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T04:40:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.30015000000000003,
      house: 0.30015000000000003,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T04:45:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.27299,
      house: 0.27299,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T04:50:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.16370666666666667,
      house: 0.16370666666666667,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T04:55:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.16180666666666665,
      house: 0.16180666666666665,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T05:00:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.16253666666666666,
      house: 0.16253666666666666,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T05:05:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.16183999999999998,
      house: 0.16183999999999998,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T05:10:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.3946733333333333,
      house: 0.3946733333333333,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T05:15:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.3402,
      house: 0.3402,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T05:20:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.3075866666666667,
      house: 0.3075866666666667,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T05:25:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.18263333333333334,
      house: 0.18263333333333334,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T05:30:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.15613666666666665,
      house: 0.15613666666666665,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T05:35:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.28558,
      house: 0.28558,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T05:40:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.20264333333333334,
      house: 0.20264333333333334,
      soc: 17.8288,
      solar: 0.0,
      timestamp: "2025-03-26T05:45:00+11:00",
    },
    {
      battery: -3.3022666666666662,
      ev: 0.0,
      grid: 3.609823333333334,
      house: 0.3075566666666667,
      soc: 19.6231,
      solar: 0.0,
      timestamp: "2025-03-26T05:50:00+11:00",
    },
    {
      battery: -4.6149000000000004,
      ev: 0.0,
      grid: 5.103683333333334,
      house: 0.48878333333333335,
      soc: 22.1291,
      solar: 0.0,
      timestamp: "2025-03-26T05:55:00+11:00",
    },
    {
      battery: -1.018,
      ev: 0.0,
      grid: 1.4393633333333333,
      house: 0.4213633333333333,
      soc: 22.6901,
      solar: 0.0,
      timestamp: "2025-03-26T06:00:00+11:00",
    },
    {
      battery: 0.22201333333333334,
      ev: 0.0,
      grid: 0.06337000000000001,
      house: 0.2853833333333333,
      soc: 22.5679,
      solar: 0.0,
      timestamp: "2025-03-26T06:05:00+11:00",
    },
    {
      battery: 0.26799666666666666,
      ev: 0.0,
      grid: 0.008403333333333332,
      house: 0.2764,
      soc: 22.4201,
      solar: 0.0,
      timestamp: "2025-03-26T06:10:00+11:00",
    },
    {
      battery: 0.20284333333333335,
      ev: 0.0,
      grid: 0.00020333333333333336,
      house: 0.20304666666666668,
      soc: 22.3069,
      solar: 0.0,
      timestamp: "2025-03-26T06:15:00+11:00",
    },
    {
      battery: 0.17751,
      ev: 0.0,
      grid: 0.003143333333333333,
      house: 0.18065333333333333,
      soc: 22.2086,
      solar: 0.0,
      timestamp: "2025-03-26T06:20:00+11:00",
    },
    {
      battery: 0.17503333333333332,
      ev: 0.0,
      grid: 0.00249,
      house: 0.17752333333333334,
      soc: 22.1114,
      solar: 0.0,
      timestamp: "2025-03-26T06:25:00+11:00",
    },
    {
      battery: 0.19238,
      ev: 0.0,
      grid: 0.008806666666666666,
      house: 0.20118666666666665,
      soc: 22.0047,
      solar: 0.0,
      timestamp: "2025-03-26T06:30:00+11:00",
    },
    {
      battery: 0.9147066666666668,
      ev: 0.0,
      grid: 0.003923333333333333,
      house: 0.91863,
      soc: 21.4956,
      solar: 0.0,
      timestamp: "2025-03-26T06:35:00+11:00",
    },
    {
      battery: 0.3798866666666667,
      ev: 0.0,
      grid: -0.0020633333333333337,
      house: 0.37782333333333334,
      soc: 21.2824,
      solar: 0.0,
      timestamp: "2025-03-26T06:40:00+11:00",
    },
    {
      battery: 0.28084666666666663,
      ev: 0.0,
      grid: 0.00097,
      house: 0.28181666666666666,
      soc: 21.1259,
      solar: 0.0,
      timestamp: "2025-03-26T06:45:00+11:00",
    },
    {
      battery: 0.2699566666666667,
      ev: 0.0,
      grid: 0.0019366666666666668,
      house: 0.2718933333333333,
      soc: 20.975,
      solar: 0.0,
      timestamp: "2025-03-26T06:50:00+11:00",
    },
    {
      battery: 0.26943666666666666,
      ev: 0.0,
      grid: 0.0015899999999999998,
      house: 0.2710266666666667,
      soc: 20.8246,
      solar: 0.0,
      timestamp: "2025-03-26T06:55:00+11:00",
    },
    {
      battery: 0.20978333333333335,
      ev: 0.0,
      grid: 0.0012799999999999999,
      house: 0.21106333333333333,
      soc: 20.7072,
      solar: 0.0,
      timestamp: "2025-03-26T07:00:00+11:00",
    },
    {
      battery: 0.21245999999999998,
      ev: 0.0,
      grid: 0.004933333333333333,
      house: 0.21739333333333333,
      soc: 20.5885,
      solar: 0.0,
      timestamp: "2025-03-26T07:05:00+11:00",
    },
    {
      battery: 0.2802766666666667,
      ev: 0.0,
      grid: 0.003906666666666667,
      house: 0.29095333333333334,
      soc: 20.4319,
      solar: 0.00677,
      timestamp: "2025-03-26T07:10:00+11:00",
    },
    {
      battery: 0.2929966666666667,
      ev: 0.0,
      grid: 0.003816666666666667,
      house: 0.3274933333333333,
      soc: 20.2678,
      solar: 0.03068,
      timestamp: "2025-03-26T07:15:00+11:00",
    },
    {
      battery: 0.22924,
      ev: 0.0,
      grid: 0.0005733333333333334,
      house: 0.30240666666666666,
      soc: 20.1396,
      solar: 0.07259333333333333,
      timestamp: "2025-03-26T07:20:00+11:00",
    },
    {
      battery: 0.17339333333333332,
      ev: 0.0,
      grid: 0.007653333333333333,
      house: 0.2962533333333333,
      soc: 20.0427,
      solar: 0.11520666666666665,
      timestamp: "2025-03-26T07:25:00+11:00",
    },
    {
      battery: 0.7249,
      ev: 0.0,
      grid: 0.00782,
      house: 0.8947433333333333,
      soc: 19.6366,
      solar: 0.16202333333333335,
      timestamp: "2025-03-26T07:30:00+11:00",
    },
    {
      battery: 0.69652,
      ev: 0.0,
      grid: 0.00024000000000000003,
      house: 0.9022233333333334,
      soc: 19.2455,
      solar: 0.20546333333333333,
      timestamp: "2025-03-26T07:35:00+11:00",
    },
    {
      battery: 0.6439400000000001,
      ev: 0.0,
      grid: 0.0016733333333333335,
      house: 0.9159966666666667,
      soc: 18.8835,
      solar: 0.2703833333333333,
      timestamp: "2025-03-26T07:40:00+11:00",
    },
    {
      battery: 0.5476466666666667,
      ev: 0.0,
      grid: 0.0018533333333333331,
      house: 0.892,
      soc: 18.5759,
      solar: 0.3425,
      timestamp: "2025-03-26T07:45:00+11:00",
    },
    {
      battery: 0.5182533333333333,
      ev: 0.0,
      grid: 0.006503333333333333,
      house: 0.9357766666666667,
      soc: 18.2854,
      solar: 0.41102,
      timestamp: "2025-03-26T07:50:00+11:00",
    },
    {
      battery: 0.91295,
      ev: 0.0,
      grid: 0.0067466666666666664,
      house: 1.3890633333333333,
      soc: 17.7712,
      solar: 0.4693666666666666,
      timestamp: "2025-03-26T07:55:00+11:00",
    },
    {
      battery: 1.3375966666666668,
      ev: 0.0,
      grid: 0.05379333333333333,
      house: 1.9222433333333333,
      soc: 17.0186,
      solar: 0.5308533333333334,
      timestamp: "2025-03-26T08:00:00+11:00",
    },
    {
      battery: 4.3192466666666665,
      ev: 0.0,
      grid: 0.06148666666666666,
      house: 4.9293933333333335,
      soc: 14.5308,
      solar: 0.54866,
      timestamp: "2025-03-26T08:05:00+11:00",
    },
    {
      battery: 4.409103333333333,
      ev: 2.4,
      grid: 1.0493733333333333,
      house: 6.111166666666666,
      soc: 11.9808,
      solar: 0.65269,
      timestamp: "2025-03-26T08:10:00+11:00",
    },
    {
      battery: 3.4125300000000003,
      ev: 2.4,
      grid: 2.20746,
      house: 6.389376666666666,
      soc: 9.98672,
      solar: 0.7693866666666667,
      timestamp: "2025-03-26T08:15:00+11:00",
    },
    {
      battery: 0.0,
      ev: 2.4,
      grid: 5.00362,
      house: 5.856473333333334,
      soc: 9.98672,
      solar: 0.8528533333333334,
      timestamp: "2025-03-26T08:20:00+11:00",
    },
    {
      battery: 0.0,
      ev: 2.4,
      grid: 4.85573,
      house: 5.802576666666667,
      soc: 9.98672,
      solar: 0.9468466666666666,
      timestamp: "2025-03-26T08:25:00+11:00",
    },
    {
      battery: 0.0,
      ev: 2.4,
      grid: 4.360720000000001,
      house: 5.474073333333333,
      soc: 9.98672,
      solar: 1.1133533333333334,
      timestamp: "2025-03-26T08:30:00+11:00",
    },
    {
      battery: 0.0,
      ev: 4.2,
      grid: 3.3170266666666666,
      house: 4.582703333333334,
      soc: 9.98672,
      solar: 1.2656766666666668,
      timestamp: "2025-03-26T08:35:00+11:00",
    },
    {
      battery: 0.0,
      ev: 4.2,
      grid: 3.2547433333333338,
      house: 4.523759999999999,
      soc: 9.98672,
      solar: 1.2690166666666667,
      timestamp: "2025-03-26T08:40:00+11:00",
    },
    {
      battery: 0.0,
      ev: 4.2,
      grid: 3.1963066666666666,
      house: 4.501273333333333,
      soc: 9.98672,
      solar: 1.3049666666666666,
      timestamp: "2025-03-26T08:45:00+11:00",
    },
    {
      battery: 0.0,
      ev: 4.2,
      grid: 3.1415833333333336,
      house: 4.503656666666666,
      soc: 9.98672,
      solar: 1.3620733333333335,
      timestamp: "2025-03-26T08:50:00+11:00",
    },
    {
      battery: 0.0,
      ev: 4.2,
      grid: 3.0724633333333333,
      house: 4.505216666666667,
      soc: 9.98672,
      solar: 1.4327533333333333,
      timestamp: "2025-03-26T08:55:00+11:00",
    },
    {
      battery: 0.0,
      ev: 4.2,
      grid: 2.960046666666667,
      house: 4.51276,
      soc: 9.98672,
      solar: 1.5527133333333332,
      timestamp: "2025-03-26T09:00:00+11:00",
    },
    {
      battery: 0.0,
      ev: 4.2,
      grid: 2.893263333333333,
      house: 4.52305,
      soc: 9.98672,
      solar: 1.6297866666666665,
      timestamp: "2025-03-26T09:05:00+11:00",
    },
    {
      battery: 0.0,
      ev: 4.2,
      grid: 2.949603333333333,
      house: 4.606543333333333,
      soc: 9.98672,
      solar: 1.65694,
      timestamp: "2025-03-26T09:10:00+11:00",
    },
    {
      battery: 0.0,
      ev: 4.2,
      grid: 2.8757266666666665,
      house: 4.607866666666666,
      soc: 9.98672,
      solar: 1.73214,
      timestamp: "2025-03-26T09:15:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 2.67518,
      house: 4.51999,
      soc: 9.98672,
      solar: 1.84481,
      timestamp: "2025-03-26T09:20:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 2.494766666666667,
      house: 4.456113333333334,
      soc: 9.98672,
      solar: 1.9613466666666668,
      timestamp: "2025-03-26T09:25:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 2.34518,
      house: 4.459956666666667,
      soc: 9.98672,
      solar: 2.1147766666666667,
      timestamp: "2025-03-26T09:30:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 2.206846666666667,
      house: 4.4562100000000004,
      soc: 9.98672,
      solar: 2.2493633333333336,
      timestamp: "2025-03-26T09:35:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 2.2028333333333334,
      house: 4.472386666666667,
      soc: 9.98672,
      solar: 2.269553333333333,
      timestamp: "2025-03-26T09:40:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 2.69755,
      house: 4.4869900000000005,
      soc: 9.98672,
      solar: 1.78944,
      timestamp: "2025-03-26T09:45:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 3.1003333333333334,
      house: 4.546906666666667,
      soc: 9.98672,
      solar: 1.4465733333333333,
      timestamp: "2025-03-26T09:50:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 1.8659233333333334,
      house: 3.3691033333333333,
      soc: 9.98672,
      solar: 1.50318,
      timestamp: "2025-03-26T09:55:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 1.3529166666666668,
      house: 2.9913833333333333,
      soc: 9.98672,
      solar: 1.6384666666666665,
      timestamp: "2025-03-26T10:00:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 1.4917633333333333,
      house: 2.9393000000000002,
      soc: 9.98672,
      solar: 1.4475366666666667,
      timestamp: "2025-03-26T10:05:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 1.6627966666666667,
      house: 2.929806666666667,
      soc: 9.98672,
      solar: 1.26701,
      timestamp: "2025-03-26T10:10:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 1.2933866666666667,
      house: 2.940243333333333,
      soc: 9.98672,
      solar: 1.6468566666666664,
      timestamp: "2025-03-26T10:15:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 1.3277766666666666,
      house: 2.93071,
      soc: 9.98672,
      solar: 1.602933333333333,
      timestamp: "2025-03-26T10:20:00+11:00",
    },
    {
      battery: 0.0,
      ev: 3.8,
      grid: 1.602666666666667,
      house: 3.0032533333333333,
      soc: 9.98672,
      solar: 1.4005866666666666,
      timestamp: "2025-03-26T10:25:00+11:00",
    },
    {
      battery: -4.024206666666666,
      ev: 3.8,
      grid: 5.826553333333333,
      house: 3.2152266666666662,
      soc: 12.1881,
      solar: 1.41288,
      timestamp: "2025-03-26T10:30:00+11:00",
    },
    {
      battery: -4.990196666666667,
      ev: 3.8,
      grid: 6.708773333333333,
      house: 3.2347300000000003,
      soc: 14.9205,
      solar: 1.5161533333333332,
      timestamp: "2025-03-26T10:35:00+11:00",
    },
    {
      battery: -4.996946666666667,
      ev: 3.8,
      grid: 6.477679999999999,
      house: 3.21214,
      soc: 17.6481,
      solar: 1.7314066666666665,
      timestamp: "2025-03-26T10:40:00+11:00",
    },
    {
      battery: -4.9966333333333335,
      ev: 3.8,
      grid: 5.85379,
      house: 3.135176666666667,
      soc: 20.3647,
      solar: 2.27802,
      timestamp: "2025-03-26T10:45:00+11:00",
    },
    {
      battery: -4.998423333333333,
      ev: 3.8,
      grid: 5.6716299999999995,
      house: 3.12193,
      soc: 23.0705,
      solar: 2.448723333333333,
      timestamp: "2025-03-26T10:50:00+11:00",
    },
    {
      battery: -4.998286666666667,
      ev: 3.8,
      grid: 5.40352,
      house: 3.1106700000000003,
      soc: 25.769,
      solar: 2.7054366666666665,
      timestamp: "2025-03-26T10:55:00+11:00",
    },
    {
      battery: -4.99653,
      ev: 3.8,
      grid: 4.9823200000000005,
      house: 3.1035899999999996,
      soc: 28.4607,
      solar: 3.1178,
      timestamp: "2025-03-26T11:00:00+11:00",
    },
    {
      battery: -4.997323333333333,
      ev: 3.8,
      grid: 4.5953800000000005,
      house: 3.195866666666667,
      soc: 31.1473,
      solar: 3.59781,
      timestamp: "2025-03-26T11:05:00+11:00",
    },
    {
      battery: -4.998396666666666,
      ev: 3.8,
      grid: 3.4562066666666666,
      house: 3.2078999999999995,
      soc: 33.8316,
      solar: 4.75009,
      timestamp: "2025-03-26T11:10:00+11:00",
    },
    {
      battery: -4.996316666666667,
      ev: 3.8,
      grid: 3.1965833333333338,
      house: 3.2087166666666667,
      soc: 36.5149,
      solar: 5.00845,
      timestamp: "2025-03-26T11:15:00+11:00",
    },
    {
      battery: -4.996533333333334,
      ev: 3.8,
      grid: 4.266683333333334,
      house: 3.197163333333333,
      soc: 39.1979,
      solar: 3.927013333333333,
      timestamp: "2025-03-26T11:20:00+11:00",
    },
    {
      battery: -4.998096666666666,
      ev: 3.8,
      grid: 4.324323333333334,
      house: 3.1350966666666666,
      soc: 41.881,
      solar: 3.8088699999999998,
      timestamp: "2025-03-26T11:25:00+11:00",
    },
    {
      battery: -1.72705,
      ev: 3.8,
      grid: 0.58669,
      house: 3.0758533333333333,
      soc: 42.8206,
      solar: 4.216213333333333,
      timestamp: "2025-03-26T11:30:00+11:00",
    },
    {
      battery: -4.696926666666666,
      ev: 3.8,
      grid: 2.1166533333333333,
      house: 3.126343333333333,
      soc: 45.337,
      solar: 5.706616666666666,
      timestamp: "2025-03-26T11:35:00+11:00",
    },
    {
      battery: -2.0619633333333334,
      ev: 2.4,
      grid: 0.29553999999999997,
      house: 3.1060533333333336,
      soc: 46.4599,
      solar: 4.872476666666667,
      timestamp: "2025-03-26T11:40:00+11:00",
    },
    {
      battery: -0.14724666666666666,
      ev: 2.4,
      grid: 0.23422666666666664,
      house: 3.0836133333333335,
      soc: 46.5412,
      solar: 2.9966333333333335,
      timestamp: "2025-03-26T11:45:00+11:00",
    },
    {
      battery: -4.46132,
      ev: 2.4,
      grid: 5.39484,
      house: 3.1817699999999998,
      soc: 48.9241,
      solar: 2.2482499999999996,
      timestamp: "2025-03-26T11:50:00+11:00",
    },
    {
      battery: -4.996993333333333,
      ev: 2.4,
      grid: 5.693806666666666,
      house: 3.2237400000000003,
      soc: 51.604,
      solar: 2.5269266666666668,
      timestamp: "2025-03-26T11:55:00+11:00",
    },
    {
      battery: -4.997646666666666,
      ev: 2.4,
      grid: 4.80994,
      house: 3.2221366666666666,
      soc: 54.2823,
      solar: 3.409843333333333,
      timestamp: "2025-03-26T12:00:00+11:00",
    },
    {
      battery: -4.99648,
      ev: 2.4,
      grid: 4.563046666666667,
      house: 3.220276666666667,
      soc: 56.9582,
      solar: 3.6537100000000002,
      timestamp: "2025-03-26T12:05:00+11:00",
    },
    {
      battery: -4.99921,
      ev: 2.4,
      grid: 3.8847866666666664,
      house: 3.00572,
      soc: 59.6348,
      solar: 4.120143333333333,
      timestamp: "2025-03-26T12:10:00+11:00",
    },
    {
      battery: -5.00003,
      ev: 2.4,
      grid: 2.5865333333333336,
      house: 2.9359433333333333,
      soc: 62.3082,
      solar: 5.3494399999999995,
      timestamp: "2025-03-26T12:15:00+11:00",
    },
    {
      battery: -4.383443333333333,
      ev: 2.4,
      grid: 0.08985333333333334,
      house: 2.97983,
      soc: 64.656,
      solar: 7.27342,
      timestamp: "2025-03-26T12:20:00+11:00",
    },
    {
      battery: -3.41094,
      ev: 2.4,
      grid: 0.04789333333333333,
      house: 2.8114033333333337,
      soc: 66.4837,
      solar: 6.17445,
      timestamp: "2025-03-26T12:25:00+11:00",
    },
    {
      battery: -4.202553333333333,
      ev: 2.4,
      grid: 0.00258,
      house: 2.6950666666666665,
      soc: 68.7313,
      solar: 6.89504,
      timestamp: "2025-03-26T12:30:00+11:00",
    },
    {
      battery: -4.11659,
      ev: 2.4,
      grid: 0.0028033333333333335,
      house: 2.66159,
      soc: 70.9298,
      solar: 6.775376666666666,
      timestamp: "2025-03-26T12:35:00+11:00",
    },
    {
      battery: -3.2399299999999998,
      ev: 2.4,
      grid: 0.01846,
      house: 2.5091466666666666,
      soc: 72.6667,
      solar: 5.730616666666666,
      timestamp: "2025-03-26T12:40:00+11:00",
    },
    {
      battery: -3.599193333333333,
      ev: 2.4,
      grid: -0.021483333333333333,
      house: 2.5440899999999997,
      soc: 74.5851,
      solar: 6.164766666666667,
      timestamp: "2025-03-26T12:45:00+11:00",
    },
    {
      battery: -2.9974533333333335,
      ev: 2.4,
      grid: 0.027346666666666665,
      house: 2.57514,
      soc: 76.1906,
      solar: 5.545246666666666,
      timestamp: "2025-03-26T12:50:00+11:00",
    },
    {
      battery: -2.95771,
      ev: 2.4,
      grid: -0.0149,
      house: 2.1212833333333334,
      soc: 77.7719,
      solar: 5.093893333333333,
      timestamp: "2025-03-26T12:55:00+11:00",
    },
    {
      battery: -4.874023333333334,
      ev: 2.8,
      grid: 0.34681666666666666,
      house: 1.4172133333333334,
      soc: 80.358,
      solar: 5.94442,
      timestamp: "2025-03-26T13:00:00+11:00",
    },
    {
      battery: -1.2275366666666665,
      ev: 2.8,
      grid: 0.5800066666666667,
      house: 3.1100233333333334,
      soc: 81.0194,
      solar: 3.7575533333333335,
      timestamp: "2025-03-26T13:05:00+11:00",
    },
    {
      battery: -4.561266666666667,
      ev: 2.8,
      grid: 2.60635,
      house: 3.126223333333334,
      soc: 83.4342,
      solar: 5.08114,
      timestamp: "2025-03-26T13:10:00+11:00",
    },
    {
      battery: -4.997293333333333,
      ev: 2.8,
      grid: 1.4550366666666668,
      house: 3.1090599999999995,
      soc: 86.0859,
      solar: 6.651316666666666,
      timestamp: "2025-03-26T13:15:00+11:00",
    },
    {
      battery: -3.8039766666666663,
      ev: 2.8,
      grid: 0.16896,
      house: 3.07239,
      soc: 88.1138,
      solar: 6.707406666666666,
      timestamp: "2025-03-26T13:20:00+11:00",
    },
    {
      battery: -3.271316666666667,
      ev: 0.0,
      grid: 0.00033,
      house: 3.044063333333334,
      soc: 89.8604,
      solar: 6.31505,
      timestamp: "2025-03-26T13:25:00+11:00",
    },
    {
      battery: -3.7237066666666667,
      ev: 0.0,
      grid: -0.01919,
      house: 2.665566666666667,
      soc: 91.8413,
      solar: 6.408463333333333,
      timestamp: "2025-03-26T13:30:00+11:00",
    },
    {
      battery: -4.395473333333333,
      ev: 0.0,
      grid: -0.0012866666666666666,
      house: 1.7417566666666668,
      soc: 94.1755,
      solar: 6.138516666666667,
      timestamp: "2025-03-26T13:35:00+11:00",
    },
    {
      battery: -4.390773333333334,
      ev: 0.0,
      grid: -8.666666666666667e-5,
      house: 1.8847899999999997,
      soc: 96.5031,
      solar: 6.275650000000001,
      timestamp: "2025-03-26T13:40:00+11:00",
    },
    {
      battery: -4.308623333333333,
      ev: 0.0,
      grid: 0.0041533333333333335,
      house: 1.8981033333333335,
      soc: 98.7832,
      solar: 6.2025733333333335,
      timestamp: "2025-03-26T13:45:00+11:00",
    },
    {
      battery: -1.9742933333333332,
      ev: 0.0,
      grid: -1.7052,
      house: 1.7871766666666664,
      soc: 99.841,
      solar: 5.46667,
      timestamp: "2025-03-26T13:50:00+11:00",
    },
    {
      battery: -0.4807,
      ev: 0.0,
      grid: -0.13297666666666666,
      house: 1.757893333333333,
      soc: 100.0,
      solar: 2.37157,
      timestamp: "2025-03-26T13:55:00+11:00",
    },
    {
      battery: -0.4799833333333333,
      ev: 0.0,
      grid: 0.3417966666666667,
      house: 1.6052166666666665,
      soc: 100.0,
      solar: 1.7434033333333332,
      timestamp: "2025-03-26T14:00:00+11:00",
    },
    {
      battery: -0.48069000000000006,
      ev: 0.0,
      grid: -1.1961066666666667,
      house: 1.7324133333333336,
      soc: 100.0,
      solar: 3.4092100000000003,
      timestamp: "2025-03-26T14:05:00+11:00",
    },
    {
      battery: -0.4810433333333334,
      ev: 0.0,
      grid: 0.5038733333333334,
      house: 1.5686166666666665,
      soc: 100.0,
      solar: 1.5457866666666664,
      timestamp: "2025-03-26T14:10:00+11:00",
    },
    {
      battery: 0.11533333333333334,
      ev: 0.0,
      grid: 0.17159666666666668,
      house: 1.6069200000000001,
      soc: 99.9038,
      solar: 1.31999,
      timestamp: "2025-03-26T14:15:00+11:00",
    },
    {
      battery: 0.22202,
      ev: 0.0,
      grid: 0.11061666666666667,
      house: 1.6691333333333334,
      soc: 99.7819,
      solar: 1.3364966666666667,
      timestamp: "2025-03-26T14:20:00+11:00",
    },
    {
      battery: 0.02528333333333333,
      ev: 0.0,
      grid: 0.18729,
      house: 1.6825433333333333,
      soc: 99.7653,
      solar: 1.46997,
      timestamp: "2025-03-26T14:25:00+11:00",
    },
    {
      battery: -0.01038,
      ev: 0.0,
      grid: 0.4692233333333333,
      house: 1.6552833333333334,
      soc: 99.771,
      solar: 1.19644,
      timestamp: "2025-03-26T14:30:00+11:00",
    },
    {
      battery: 0.47797666666666666,
      ev: 0.0,
      grid: 0.16607333333333332,
      house: 1.83202,
      soc: 99.7612,
      solar: 1.18797,
      timestamp: "2025-03-26T14:35:00+11:00",
    },
    {
      battery: 0.04724,
      ev: 0.0,
      grid: 0.5584233333333334,
      house: 2.5333533333333333,
      soc: 99.7365,
      solar: 1.9276900000000001,
      timestamp: "2025-03-26T14:40:00+11:00",
    },
    {
      battery: -0.00439,
      ev: 0.0,
      grid: 0.5414166666666667,
      house: 2.6195833333333334,
      soc: 99.7389,
      solar: 2.0825566666666666,
      timestamp: "2025-03-26T14:45:00+11:00",
    },
    {
      battery: -0.06840333333333334,
      ev: 0.0,
      grid: 0.4168766666666667,
      house: 2.6358266666666665,
      soc: 99.7762,
      solar: 2.2873533333333333,
      timestamp: "2025-03-26T14:50:00+11:00",
    },
    {
      battery: -0.03704333333333333,
      ev: 0.0,
      grid: 0.4335466666666667,
      house: 2.734636666666667,
      soc: 99.7962,
      solar: 2.3381333333333334,
      timestamp: "2025-03-26T14:55:00+11:00",
    },
    {
      battery: -0.3130466666666667,
      ev: 0.0,
      grid: -0.10657,
      house: 2.77751,
      soc: 99.9647,
      solar: 3.1971266666666667,
      timestamp: "2025-03-26T15:00:00+11:00",
    },
    {
      battery: -0.4761833333333333,
      ev: 0.0,
      grid: -0.35207666666666665,
      house: 2.6728766666666663,
      soc: 100.0,
      solar: 3.5011366666666666,
      timestamp: "2025-03-26T15:05:00+11:00",
    },
    {
      battery: -0.29181666666666667,
      ev: 0.0,
      grid: -0.06275,
      house: 2.678506666666667,
      soc: 100.0,
      solar: 3.0330733333333333,
      timestamp: "2025-03-26T15:10:00+11:00",
    },
    {
      battery: -0.06375666666666666,
      ev: 0.0,
      grid: 1.7141600000000001,
      house: 4.41205,
      soc: 100.0,
      solar: 2.7616466666666666,
      timestamp: "2025-03-26T15:15:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 1.7909933333333332,
      house: 4.31715,
      soc: 100.0,
      solar: 2.5261566666666666,
      timestamp: "2025-03-26T15:20:00+11:00",
    },
    {
      battery: -0.18457666666666667,
      ev: 0.0,
      grid: 0.4378766666666667,
      house: 2.70594,
      soc: 100.0,
      solar: 2.4526399999999997,
      timestamp: "2025-03-26T15:25:00+11:00",
    },
    {
      battery: -0.4822333333333333,
      ev: 0.0,
      grid: -0.5246433333333334,
      house: 1.45689,
      soc: 100.0,
      solar: 2.4637666666666664,
      timestamp: "2025-03-26T15:30:00+11:00",
    },
    {
      battery: -0.3550333333333333,
      ev: 0.0,
      grid: -0.25404333333333334,
      house: 2.1854466666666665,
      soc: 100.0,
      solar: 2.7945233333333332,
      timestamp: "2025-03-26T15:35:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 1.0325333333333333,
      house: 3.6741366666666666,
      soc: 100.0,
      solar: 2.6416033333333333,
      timestamp: "2025-03-26T15:40:00+11:00",
    },
    {
      battery: -0.4673,
      ev: 0.0,
      grid: -0.45388,
      house: 1.5631666666666668,
      soc: 100.0,
      solar: 2.4843466666666667,
      timestamp: "2025-03-26T15:45:00+11:00",
    },
    {
      battery: -0.19449666666666665,
      ev: 0.0,
      grid: -0.64393,
      house: 1.6235666666666668,
      soc: 100.0,
      solar: 2.461993333333333,
      timestamp: "2025-03-26T15:50:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.6405266666666667,
      house: 2.6865966666666665,
      soc: 100.0,
      solar: 2.04607,
      timestamp: "2025-03-26T15:55:00+11:00",
    },
    {
      battery: 0.6673233333333334,
      ev: 0.0,
      grid: 0.07547666666666666,
      house: 2.608773333333333,
      soc: 99.6376,
      solar: 1.8659733333333335,
      timestamp: "2025-03-26T16:00:00+11:00",
    },
    {
      battery: 1.5349533333333332,
      ev: 0.0,
      grid: 0.021456666666666666,
      house: 3.4491533333333333,
      soc: 98.8014,
      solar: 1.8927433333333332,
      timestamp: "2025-03-26T16:05:00+11:00",
    },
    {
      battery: 0.5145500000000001,
      ev: 0.0,
      grid: -0.20101333333333335,
      house: 2.1330533333333332,
      soc: 98.5202,
      solar: 1.8195166666666664,
      timestamp: "2025-03-26T16:10:00+11:00",
    },
    {
      battery: 0.3076333333333333,
      ev: 0.0,
      grid: 0.20657333333333333,
      house: 2.18032,
      soc: 98.3484,
      solar: 1.6661133333333336,
      timestamp: "2025-03-26T16:15:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.05324999999999999,
      house: 1.3375066666666666,
      soc: 98.3484,
      solar: 1.2842566666666666,
      timestamp: "2025-03-26T16:20:00+11:00",
    },
    {
      battery: 0.35339333333333334,
      ev: 0.0,
      grid: 0.03472666666666667,
      house: 1.23572,
      soc: 98.1571,
      solar: 0.8476000000000001,
      timestamp: "2025-03-26T16:25:00+11:00",
    },
    {
      battery: 0.6316033333333333,
      ev: 0.0,
      grid: 0.0077933333333333335,
      house: 1.2261833333333334,
      soc: 97.8127,
      solar: 0.5867866666666667,
      timestamp: "2025-03-26T16:30:00+11:00",
    },
    {
      battery: 0.5444666666666667,
      ev: 0.0,
      grid: 0.00098,
      house: 1.2291633333333332,
      soc: 97.5155,
      solar: 0.6837166666666666,
      timestamp: "2025-03-26T16:35:00+11:00",
    },
    {
      battery: 0.6070866666666667,
      ev: 0.0,
      grid: 0.0035633333333333333,
      house: 1.2226366666666666,
      soc: 97.185,
      solar: 0.6119866666666667,
      timestamp: "2025-03-26T16:40:00+11:00",
    },
    {
      battery: 0.7626066666666667,
      ev: 0.0,
      grid: 0.0034033333333333333,
      house: 1.2343733333333333,
      soc: 96.7693,
      solar: 0.4683633333333333,
      timestamp: "2025-03-26T16:45:00+11:00",
    },
    {
      battery: 0.9299266666666667,
      ev: 0.0,
      grid: 0.004236666666666666,
      house: 1.42163,
      soc: 96.2621,
      solar: 0.4874666666666666,
      timestamp: "2025-03-26T16:50:00+11:00",
    },
    {
      battery: 1.0654366666666666,
      ev: 0.0,
      grid: 0.00396,
      house: 1.4993400000000001,
      soc: 95.68,
      solar: 0.42994333333333334,
      timestamp: "2025-03-26T16:55:00+11:00",
    },
    {
      battery: 1.0535933333333334,
      ev: 0.0,
      grid: -0.00013000000000000002,
      house: 1.4959133333333334,
      soc: 95.1046,
      solar: 0.44245,
      timestamp: "2025-03-26T17:00:00+11:00",
    },
    {
      battery: 0.9422600000000001,
      ev: 0.0,
      grid: 0.0016233333333333334,
      house: 1.4088733333333334,
      soc: 94.5904,
      solar: 0.46499,
      timestamp: "2025-03-26T17:05:00+11:00",
    },
    {
      battery: 0.8558100000000001,
      ev: 0.0,
      grid: -0.0012833333333333334,
      house: 1.2623199999999999,
      soc: 94.123,
      solar: 0.40779333333333334,
      timestamp: "2025-03-26T17:10:00+11:00",
    },
    {
      battery: 0.7513766666666667,
      ev: 0.0,
      grid: 0.00894,
      house: 1.17695,
      soc: 93.7134,
      solar: 0.4166333333333333,
      timestamp: "2025-03-26T17:15:00+11:00",
    },
    {
      battery: 0.87776,
      ev: 0.0,
      grid: 0.004753333333333333,
      house: 1.2145299999999999,
      soc: 93.2351,
      solar: 0.3320166666666667,
      timestamp: "2025-03-26T17:20:00+11:00",
    },
    {
      battery: 0.83707,
      ev: 0.0,
      grid: 4.3333333333333334e-5,
      house: 1.1452900000000001,
      soc: 92.7782,
      solar: 0.30817666666666665,
      timestamp: "2025-03-26T17:25:00+11:00",
    },
    {
      battery: 0.9093266666666666,
      ev: 0.0,
      grid: 0.0067933333333333335,
      house: 1.20179,
      soc: 92.282,
      solar: 0.28567,
      timestamp: "2025-03-26T17:30:00+11:00",
    },
    {
      battery: 0.96208,
      ev: 0.0,
      grid: 0.00315,
      house: 1.1964299999999999,
      soc: 91.7569,
      solar: 0.23120000000000002,
      timestamp: "2025-03-26T17:35:00+11:00",
    },
    {
      battery: 0.9033966666666667,
      ev: 0.0,
      grid: -0.003306666666666667,
      house: 1.0577466666666666,
      soc: 91.2635,
      solar: 0.15765666666666667,
      timestamp: "2025-03-26T17:40:00+11:00",
    },
    {
      battery: 0.6296266666666667,
      ev: 0.0,
      grid: 0.00027333333333333333,
      house: 0.8525966666666667,
      soc: 90.9194,
      solar: 0.22269666666666668,
      timestamp: "2025-03-26T17:45:00+11:00",
    },
    {
      battery: 2.0223266666666664,
      ev: 0.0,
      grid: 0.02400666666666667,
      house: 2.3937933333333334,
      soc: 89.8139,
      solar: 0.34746,
      timestamp: "2025-03-26T17:50:00+11:00",
    },
    {
      battery: 1.0882399999999999,
      ev: 0.0,
      grid: -0.025586666666666667,
      house: 1.4383066666666666,
      soc: 89.2149,
      solar: 0.3756533333333334,
      timestamp: "2025-03-26T17:55:00+11:00",
    },
    {
      battery: 0.34642666666666666,
      ev: 0.0,
      grid: 0.007276666666666666,
      house: 0.7351933333333334,
      soc: 89.0265,
      solar: 0.38149,
      timestamp: "2025-03-26T18:00:00+11:00",
    },
    {
      battery: 0.6765633333333334,
      ev: 0.0,
      grid: 0.012223333333333334,
      house: 0.8898499999999999,
      soc: 88.6586,
      solar: 0.20106333333333334,
      timestamp: "2025-03-26T18:05:00+11:00",
    },
    {
      battery: 0.8008566666666666,
      ev: 0.0,
      grid: 0.013499999999999998,
      house: 0.9762200000000001,
      soc: 88.2214,
      solar: 0.16186333333333333,
      timestamp: "2025-03-26T18:10:00+11:00",
    },
    {
      battery: 1.0412033333333333,
      ev: 0.0,
      grid: 0.00662,
      house: 1.2409033333333335,
      soc: 87.6532,
      solar: 0.19308,
      timestamp: "2025-03-26T18:15:00+11:00",
    },
    {
      battery: 1.0144099999999998,
      ev: 0.0,
      grid: 0.008706666666666666,
      house: 1.20936,
      soc: 87.0992,
      solar: 0.18624333333333332,
      timestamp: "2025-03-26T18:20:00+11:00",
    },
    {
      battery: 2.372986666666667,
      ev: 0.0,
      grid: 0.02877333333333333,
      house: 2.5691,
      soc: 85.7984,
      solar: 0.16734000000000002,
      timestamp: "2025-03-26T18:25:00+11:00",
    },
    {
      battery: 4.1450233333333335,
      ev: 0.0,
      grid: 0.34165999999999996,
      house: 4.61418,
      soc: 83.5094,
      solar: 0.12749666666666667,
      timestamp: "2025-03-26T18:30:00+11:00",
    },
    {
      battery: 1.9938633333333333,
      ev: 0.0,
      grid: -0.03689666666666667,
      house: 2.03371,
      soc: 82.4108,
      solar: 0.07674333333333333,
      timestamp: "2025-03-26T18:35:00+11:00",
    },
    {
      battery: 2.0214766666666666,
      ev: 0.0,
      grid: -0.0167,
      house: 2.0471500000000002,
      soc: 81.3018,
      solar: 0.04237333333333333,
      timestamp: "2025-03-26T18:40:00+11:00",
    },
    {
      battery: 1.4952766666666668,
      ev: 0.0,
      grid: -0.0037466666666666664,
      house: 1.50969,
      soc: 80.4831,
      solar: 0.01816,
      timestamp: "2025-03-26T18:45:00+11:00",
    },
    {
      battery: 1.8887066666666665,
      ev: 0.0,
      grid: 0.019176666666666665,
      house: 1.9078833333333334,
      soc: 79.4475,
      solar: 0.0,
      timestamp: "2025-03-26T18:50:00+11:00",
    },
    {
      battery: 2.85677,
      ev: 0.0,
      grid: 0.12424333333333332,
      house: 2.9810133333333333,
      soc: 77.8729,
      solar: 0.0,
      timestamp: "2025-03-26T18:55:00+11:00",
    },
    {
      battery: 1.4397133333333334,
      ev: 0.0,
      grid: 0.004253333333333333,
      house: 1.4439666666666666,
      soc: 77.0841,
      solar: 0.0,
      timestamp: "2025-03-26T19:00:00+11:00",
    },
    {
      battery: 1.8350933333333335,
      ev: 0.0,
      grid: 0.04721,
      house: 1.8823033333333332,
      soc: 76.0756,
      solar: 0.0,
      timestamp: "2025-03-26T19:05:00+11:00",
    },
    {
      battery: 1.2388366666666668,
      ev: 0.0,
      grid: 0.028130000000000002,
      house: 1.2669666666666666,
      soc: 75.3966,
      solar: 0.0,
      timestamp: "2025-03-26T19:10:00+11:00",
    },
    {
      battery: 0.91478,
      ev: 0.0,
      grid: 0.0020133333333333336,
      house: 0.9167933333333333,
      soc: 74.8967,
      solar: 0.0,
      timestamp: "2025-03-26T19:15:00+11:00",
    },
    {
      battery: 0.94505,
      ev: 0.0,
      grid: 0.0021666666666666666,
      house: 0.9472166666666667,
      soc: 74.3798,
      solar: 0.0,
      timestamp: "2025-03-26T19:20:00+11:00",
    },
    {
      battery: 1.0504266666666666,
      ev: 0.0,
      grid: 0.0034666666666666665,
      house: 1.0538933333333333,
      soc: 73.8054,
      solar: 0.0,
      timestamp: "2025-03-26T19:25:00+11:00",
    },
    {
      battery: 1.3284099999999999,
      ev: 0.0,
      grid: 0.011786666666666668,
      house: 1.3401966666666665,
      soc: 73.0787,
      solar: 0.0,
      timestamp: "2025-03-26T19:30:00+11:00",
    },
    {
      battery: 1.4467599999999998,
      ev: 0.0,
      grid: 0.005053333333333333,
      house: 1.4518133333333334,
      soc: 72.2856,
      solar: 0.0,
      timestamp: "2025-03-26T19:35:00+11:00",
    },
    {
      battery: 1.3458133333333333,
      ev: 0.0,
      grid: 0.011933333333333334,
      house: 1.3577466666666667,
      soc: 71.5475,
      solar: 0.0,
      timestamp: "2025-03-26T19:40:00+11:00",
    },
    {
      battery: 1.5723333333333334,
      ev: 0.0,
      grid: 0.005166666666666667,
      house: 1.5775000000000001,
      soc: 70.6833,
      solar: 0.0,
      timestamp: "2025-03-26T19:45:00+11:00",
    },
    {
      battery: 1.7590766666666666,
      ev: 0.0,
      grid: 0.00411,
      house: 1.7631866666666665,
      soc: 69.7153,
      solar: 0.0,
      timestamp: "2025-03-26T19:50:00+11:00",
    },
    {
      battery: 1.7668266666666668,
      ev: 0.0,
      grid: 0.0031233333333333334,
      house: 1.76995,
      soc: 68.7405,
      solar: 0.0,
      timestamp: "2025-03-26T19:55:00+11:00",
    },
    {
      battery: 1.24641,
      ev: 0.0,
      grid: 0.00021666666666666666,
      house: 1.2466266666666665,
      soc: 68.0538,
      solar: 0.0,
      timestamp: "2025-03-26T20:00:00+11:00",
    },
    {
      battery: 1.2001666666666666,
      ev: 0.0,
      grid: 0.0007099999999999999,
      house: 1.2008766666666666,
      soc: 67.3924,
      solar: 0.0,
      timestamp: "2025-03-26T20:05:00+11:00",
    },
    {
      battery: 1.20346,
      ev: 0.0,
      grid: -0.00012666666666666666,
      house: 1.2033333333333331,
      soc: 66.7299,
      solar: 0.0,
      timestamp: "2025-03-26T20:10:00+11:00",
    },
    {
      battery: 1.1224966666666667,
      ev: 0.0,
      grid: 0.0015666666666666667,
      house: 1.1240633333333334,
      soc: 66.1117,
      solar: 0.0,
      timestamp: "2025-03-26T20:15:00+11:00",
    },
    {
      battery: 1.0225833333333334,
      ev: 0.0,
      grid: -0.001966666666666667,
      house: 1.0206166666666667,
      soc: 65.5482,
      solar: 0.0,
      timestamp: "2025-03-26T20:20:00+11:00",
    },
    {
      battery: 1.673293333333333,
      ev: 0.0,
      grid: 0.03022666666666667,
      house: 1.7035200000000001,
      soc: 64.6259,
      solar: 0.0,
      timestamp: "2025-03-26T20:25:00+11:00",
    },
    {
      battery: 1.0446533333333334,
      ev: 0.0,
      grid: -0.012506666666666666,
      house: 1.0321466666666668,
      soc: 64.0454,
      solar: 0.0,
      timestamp: "2025-03-26T20:30:00+11:00",
    },
    {
      battery: 1.0203133333333332,
      ev: 0.0,
      grid: -2.3333333333333332e-5,
      house: 1.02029,
      soc: 63.482,
      solar: 0.0,
      timestamp: "2025-03-26T20:35:00+11:00",
    },
    {
      battery: 1.25006,
      ev: 0.0,
      grid: 0.02255666666666667,
      house: 1.2726166666666667,
      soc: 62.7913,
      solar: 0.0,
      timestamp: "2025-03-26T20:40:00+11:00",
    },
    {
      battery: 1.1931733333333332,
      ev: 0.0,
      grid: 0.0046166666666666665,
      house: 1.1977900000000001,
      soc: 62.1325,
      solar: 0.0,
      timestamp: "2025-03-26T20:45:00+11:00",
    },
    {
      battery: 1.1881066666666666,
      ev: 0.0,
      grid: 0.0032933333333333334,
      house: 1.1914,
      soc: 61.4766,
      solar: 0.0,
      timestamp: "2025-03-26T20:50:00+11:00",
    },
    {
      battery: 1.1022566666666667,
      ev: 0.0,
      grid: 0.0036633333333333335,
      house: 1.10592,
      soc: 60.8678,
      solar: 0.0,
      timestamp: "2025-03-26T20:55:00+11:00",
    },
    {
      battery: 0.12866,
      ev: 0.0,
      grid: 0.91534,
      house: 1.044,
      soc: 60.7948,
      solar: 0.0,
      timestamp: "2025-03-26T21:00:00+11:00",
    },
    {
      battery: 0.79673,
      ev: 0.0,
      grid: 0.11648333333333334,
      house: 0.9132133333333332,
      soc: 60.3569,
      solar: 0.0,
      timestamp: "2025-03-26T21:05:00+11:00",
    },
    {
      battery: 0.7896466666666666,
      ev: 0.0,
      grid: 0.0013,
      house: 0.7909466666666667,
      soc: 59.9206,
      solar: 0.0,
      timestamp: "2025-03-26T21:10:00+11:00",
    },
    {
      battery: 0.6449699999999999,
      ev: 0.0,
      grid: 0.0011366666666666667,
      house: 0.6461066666666666,
      soc: 59.5643,
      solar: 0.0,
      timestamp: "2025-03-26T21:15:00+11:00",
    },
    {
      battery: 0.75044,
      ev: 0.0,
      grid: 0.0109,
      house: 0.76134,
      soc: 59.1502,
      solar: 0.0,
      timestamp: "2025-03-26T21:20:00+11:00",
    },
    {
      battery: 0.79864,
      ev: 0.0,
      grid: 0.0010333333333333332,
      house: 0.7996733333333333,
      soc: 58.7085,
      solar: 0.0,
      timestamp: "2025-03-26T21:25:00+11:00",
    },
    {
      battery: 0.08104333333333333,
      ev: 0.0,
      grid: 0.5153466666666666,
      house: 0.59639,
      soc: 58.6622,
      solar: 0.0,
      timestamp: "2025-03-26T21:30:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.4761666666666666,
      house: 0.4761666666666666,
      soc: 58.6622,
      solar: 0.0,
      timestamp: "2025-03-26T21:35:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.53094,
      house: 0.53094,
      soc: 58.6622,
      solar: 0.0,
      timestamp: "2025-03-26T21:40:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.5660233333333333,
      house: 0.5660233333333333,
      soc: 58.6622,
      solar: 0.0,
      timestamp: "2025-03-26T21:45:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.57386,
      house: 0.57386,
      soc: 58.6622,
      solar: 0.0,
      timestamp: "2025-03-26T21:50:00+11:00",
    },
    {
      battery: 0.0,
      ev: 0.0,
      grid: 0.4098733333333333,
      house: 0.4098733333333333,
      soc: 58.6622,
      solar: 0.0,
      timestamp: "2025-03-26T21:55:00+11:00",
    },
    {
      battery: 0.5152733333333334,
      ev: 0.0,
      grid: 0.05909666666666666,
      house: 0.57437,
      soc: 58.3792,
      solar: 0.0,
      timestamp: "2025-03-26T22:00:00+11:00",
    },
    {
      battery: 0.5221233333333334,
      ev: 0.0,
      grid: 0.00014,
      house: 0.5222633333333333,
      soc: 58.0908,
      solar: 0.0,
      timestamp: "2025-03-26T22:05:00+11:00",
    },
    {
      battery: 0.4023,
      ev: 0.0,
      grid: 0.0024333333333333334,
      house: 0.4047333333333334,
      soc: 57.8684,
      solar: 0.0,
      timestamp: "2025-03-26T22:10:00+11:00",
    },
    {
      battery: 0.52136,
      ev: 0.0,
      grid: -0.012976666666666668,
      house: 0.5083833333333334,
      soc: 57.5809,
      solar: 0.0,
      timestamp: "2025-03-26T22:15:00+11:00",
    },
    {
      battery: 0.6424133333333334,
      ev: 0.0,
      grid: 0.0013533333333333333,
      house: 0.6437666666666666,
      soc: 57.2254,
      solar: 0.0,
      timestamp: "2025-03-26T22:20:00+11:00",
    },
    {
      battery: 0.5084366666666666,
      ev: 0.0,
      grid: 0.00117,
      house: 0.5096066666666668,
      soc: 56.9444,
      solar: 0.0,
      timestamp: "2025-03-26T22:25:00+11:00",
    },
    {
      battery: 0.5043300000000001,
      ev: 0.0,
      grid: 0.0023466666666666666,
      house: 0.5066766666666667,
      soc: 56.6654,
      solar: 0.0,
      timestamp: "2025-03-26T22:30:00+11:00",
    },
    {
      battery: 0.5115833333333333,
      ev: 0.0,
      grid: 0.01033,
      house: 0.5219133333333333,
      soc: 56.3827,
      solar: 0.0,
      timestamp: "2025-03-26T22:35:00+11:00",
    },
    {
      battery: 0.49707999999999997,
      ev: 0.0,
      grid: 0.0021166666666666664,
      house: 0.49919666666666673,
      soc: 56.1082,
      solar: 0.0,
      timestamp: "2025-03-26T22:40:00+11:00",
    },
    {
      battery: 0.48308666666666666,
      ev: 0.0,
      grid: 0.0036333333333333335,
      house: 0.48672,
      soc: 55.8407,
      solar: 0.0,
      timestamp: "2025-03-26T22:45:00+11:00",
    },
    {
      battery: 0.5217033333333334,
      ev: 0.0,
      grid: 0.00581,
      house: 0.5275133333333334,
      soc: 55.5517,
      solar: 0.0,
      timestamp: "2025-03-26T22:50:00+11:00",
    },
    {
      battery: 0.3907233333333333,
      ev: 0.0,
      grid: 0.003033333333333333,
      house: 0.39375666666666664,
      soc: 55.3356,
      solar: 0.0,
      timestamp: "2025-03-26T22:55:00+11:00",
    },
    {
      battery: 0.09969666666666668,
      ev: 0.0,
      grid: 0.4113866666666667,
      house: 0.5110833333333333,
      soc: 55.2798,
      solar: 0.0,
      timestamp: "2025-03-26T23:00:00+11:00",
    },
    {
      battery: 0.4119666666666667,
      ev: 0.0,
      grid: 0.10919333333333334,
      house: 0.5211600000000001,
      soc: 55.053,
      solar: 0.0,
      timestamp: "2025-03-26T23:05:00+11:00",
    },
    {
      battery: 0.6738366666666666,
      ev: 0.0,
      grid: 0.00792,
      house: 0.6817566666666667,
      soc: 54.6813,
      solar: 0.0,
      timestamp: "2025-03-26T23:10:00+11:00",
    },
    {
      battery: 0.6686966666666667,
      ev: 0.0,
      grid: 0.007526666666666668,
      house: 0.6762233333333334,
      soc: 54.3108,
      solar: 0.0,
      timestamp: "2025-03-26T23:15:00+11:00",
    },
    {
      battery: 0.6348866666666667,
      ev: 0.0,
      grid: 0.0013166666666666667,
      house: 0.6362033333333333,
      soc: 53.9595,
      solar: 0.0,
      timestamp: "2025-03-26T23:20:00+11:00",
    },
    {
      battery: 0.41374999999999995,
      ev: 0.0,
      grid: -0.0032700000000000003,
      house: 0.41048000000000007,
      soc: 53.7302,
      solar: 0.0,
      timestamp: "2025-03-26T23:25:00+11:00",
    },
    {
      battery: 0.25286,
      ev: 0.0,
      grid: 0.007209999999999999,
      house: 0.26007,
      soc: 53.5909,
      solar: 0.0,
      timestamp: "2025-03-26T23:30:00+11:00",
    },
    {
      battery: 0.33657,
      ev: 0.0,
      grid: 0.0026433333333333335,
      house: 0.3392133333333333,
      soc: 53.405,
      solar: 0.0,
      timestamp: "2025-03-26T23:35:00+11:00",
    },
    {
      battery: 0.33122,
      ev: 0.0,
      grid: 0.0023666666666666667,
      house: 0.3335866666666667,
      soc: 53.2225,
      solar: 0.0,
      timestamp: "2025-03-26T23:40:00+11:00",
    },
    {
      battery: 0.29610333333333333,
      ev: 0.0,
      grid: 0.003853333333333333,
      house: 0.29995666666666665,
      soc: 53.0588,
      solar: 0.0,
      timestamp: "2025-03-26T23:45:00+11:00",
    },
    {
      battery: 0.22549666666666668,
      ev: 0.0,
      grid: 0.00262,
      house: 0.2281166666666667,
      soc: 52.9342,
      solar: 0.0,
      timestamp: "2025-03-26T23:50:00+11:00",
    },
    {
      battery: 0.2993033333333333,
      ev: 0.0,
      grid: 0.00648,
      house: 0.30578333333333335,
      soc: 52.7692,
      solar: 0.0,
      timestamp: "2025-03-26T23:55:00+11:00",
    },
  ];
};

const getWeekData = () => {
  return [
    {
      battery: 0.3491211111111111,
      ev: 0.0,
      grid: 0.003063888888888889,
      house: 0.352185,
      soc: 12.135750000000002,
      solar: 0.0,
      timestamp: "2025-02-03T00:00:00+11:00",
    },
    {
      battery: 0.34208333333333335,
      ev: 0.0,
      grid: 0.003087222222222222,
      house: 0.34517055555555554,
      soc: 10.902216666666666,
      solar: 0.0,
      timestamp: "2025-02-03T00:30:00+11:00",
    },
    {
      battery: 0.10388055555555556,
      ev: 0.0,
      grid: 0.21531,
      house: 0.31919055555555553,
      soc: 10.03787,
      solar: 0.0,
      timestamp: "2025-02-03T01:00:00+11:00",
    },
    {
      battery: -0.014052777777777779,
      ev: 0.0,
      grid: 0.31529944444444447,
      house: 0.30124666666666666,
      soc: 10.05965,
      solar: 0.0,
      timestamp: "2025-02-03T01:30:00+11:00",
    },
    {
      battery: -0.011725,
      ev: 0.0,
      grid: 0.3262138888888889,
      house: 0.3144888888888889,
      soc: 10.10645,
      solar: 0.0,
      timestamp: "2025-02-03T02:00:00+11:00",
    },
    {
      battery: -0.0104,
      ev: 0.0,
      grid: 0.28751055555555555,
      house: 0.2771105555555556,
      soc: 10.146183333333333,
      solar: 0.0,
      timestamp: "2025-02-03T02:30:00+11:00",
    },
    {
      battery: -0.009913888888888889,
      ev: 0.0,
      grid: 0.29103277777777775,
      house: 0.28111888888888886,
      soc: 10.183016666666665,
      solar: 0.0,
      timestamp: "2025-02-03T03:00:00+11:00",
    },
    {
      battery: -0.009560555555555555,
      ev: 0.0,
      grid: 0.329545,
      house: 0.31998444444444446,
      soc: 10.2186,
      solar: 0.0,
      timestamp: "2025-02-03T03:30:00+11:00",
    },
    {
      battery: -0.009351666666666666,
      ev: 0.0,
      grid: 0.35434666666666664,
      house: 0.344995,
      soc: 10.253383333333334,
      solar: 0.0,
      timestamp: "2025-02-03T04:00:00+11:00",
    },
    {
      battery: -0.009093333333333333,
      ev: 0.0,
      grid: 0.3078488888888889,
      house: 0.29875555555555555,
      soc: 10.287483333333334,
      solar: 0.0,
      timestamp: "2025-02-03T04:30:00+11:00",
    },
    {
      battery: -0.008976666666666668,
      ev: 0.0,
      grid: 0.2587388888888889,
      house: 0.24976222222222222,
      soc: 10.3209,
      solar: 0.0,
      timestamp: "2025-02-03T05:00:00+11:00",
    },
    {
      battery: -0.008936666666666667,
      ev: 0.0,
      grid: 0.3038372222222222,
      house: 0.29490055555555555,
      soc: 10.354016666666668,
      solar: 0.0,
      timestamp: "2025-02-03T05:30:00+11:00",
    },
    {
      battery: -0.00870888888888889,
      ev: 0.0,
      grid: 0.30493,
      house: 0.29819833333333334,
      soc: 10.38695,
      solar: 0.0019772222222222222,
      timestamp: "2025-02-03T06:00:00+11:00",
    },
    {
      battery: -0.008247222222222222,
      ev: 0.0,
      grid: 0.11871888888888889,
      house: 0.2648511111111111,
      soc: 10.41885,
      solar: 0.15437944444444446,
      timestamp: "2025-02-03T06:30:00+11:00",
    },
    {
      battery: -0.008091111111111111,
      ev: 0.0,
      grid: 0.012309444444444444,
      house: 0.35078944444444443,
      soc: 10.449866666666667,
      solar: 0.3465711111111111,
      timestamp: "2025-02-03T07:00:00+11:00",
    },
    {
      battery: -0.27300444444444444,
      ev: 0.0,
      grid: 5.555555555555555e-7,
      house: 0.3392483333333333,
      soc: 10.879166666666668,
      solar: 0.6122522222222222,
      timestamp: "2025-02-03T07:30:00+11:00",
    },
    {
      battery: -0.02409277777777778,
      ev: 0.0,
      grid: 2.5437061111111112,
      house: 3.8624244444444447,
      soc: 11.476483333333334,
      solar: 1.3428111111111112,
      timestamp: "2025-02-03T08:00:00+11:00",
    },
    {
      battery: -0.04531222222222222,
      ev: 0.0,
      grid: 0.05878944444444444,
      house: 1.6752994444444445,
      soc: 11.532200000000001,
      solar: 1.6618222222222223,
      timestamp: "2025-02-03T08:30:00+11:00",
    },
    {
      battery: -0.4532555555555556,
      ev: 0.0,
      grid: -0.002522222222222222,
      house: 1.6554044444444445,
      soc: 12.338933333333335,
      solar: 2.1111822222222223,
      timestamp: "2025-02-03T09:00:00+11:00",
    },
    {
      battery: -1.2294227777777778,
      ev: 0.0,
      grid: -0.00203,
      house: 1.678811111111111,
      soc: 15.3956,
      solar: 2.9102638888888888,
      timestamp: "2025-02-03T09:30:00+11:00",
    },
    {
      battery: -2.2900666666666667,
      ev: 0.0,
      grid: -0.003946666666666667,
      house: 1.6930777777777777,
      soc: 21.47896666666667,
      solar: 3.987091111111111,
      timestamp: "2025-02-03T10:00:00+11:00",
    },
    {
      battery: -3.805893333333333,
      ev: 0.0,
      grid: -0.012946666666666667,
      house: 1.7499638888888889,
      soc: 32.148183333333336,
      solar: 5.568803888888889,
      timestamp: "2025-02-03T10:30:00+11:00",
    },
    {
      battery: -4.364862222222222,
      ev: 0.0,
      grid: -0.009388333333333334,
      house: 1.7074827777777777,
      soc: 45.03145000000001,
      solar: 6.081733333333333,
      timestamp: "2025-02-03T11:00:00+11:00",
    },
    {
      battery: -4.79718,
      ev: 0.0,
      grid: -0.003427777777777778,
      house: 1.7163866666666667,
      soc: 60.161750000000005,
      solar: 6.516994444444444,
      timestamp: "2025-02-03T11:30:00+11:00",
    },
    {
      battery: -4.884325,
      ev: 0.0,
      grid: -0.0018894444444444445,
      house: 1.888943888888889,
      soc: 75.7234,
      solar: 6.775158333333334,
      timestamp: "2025-02-03T12:00:00+11:00",
    },
    {
      battery: -4.990960555555556,
      ev: 0.0,
      grid: -0.3137405555555556,
      house: 1.6482783333333333,
      soc: 91.49686666666668,
      solar: 6.952979444444445,
      timestamp: "2025-02-03T12:30:00+11:00",
    },
    {
      battery: -2.9753444444444446,
      ev: 0.0,
      grid: -0.5827322222222222,
      house: 3.0218444444444446,
      soc: 100.0,
      solar: 6.579921111111111,
      timestamp: "2025-02-03T13:00:00+11:00",
    },
    {
      battery: -0.44461,
      ev: 0.0,
      grid: -3.1549455555555554,
      house: 1.8269972222222222,
      soc: 100.0,
      solar: 5.426552777777777,
      timestamp: "2025-02-03T13:30:00+11:00",
    },
    {
      battery: -0.0030044444444444446,
      ev: 0.0,
      grid: -1.9173683333333333,
      house: 2.9354116666666665,
      soc: 99.99368333333335,
      solar: 4.855784444444445,
      timestamp: "2025-02-03T14:00:00+11:00",
    },
    {
      battery: 0.09190166666666667,
      ev: 0.0,
      grid: -0.48813555555555554,
      house: 3.572052777777778,
      soc: 99.73191666666666,
      solar: 3.9682866666666667,
      timestamp: "2025-02-03T14:30:00+11:00",
    },
    {
      battery: 0.027449444444444443,
      ev: 0.0,
      grid: -0.33911722222222224,
      house: 2.938518888888889,
      soc: 99.66948333333333,
      solar: 3.2501866666666666,
      timestamp: "2025-02-03T15:00:00+11:00",
    },
    {
      battery: 0.14452944444444443,
      ev: 0.0,
      grid: -0.689635,
      house: 2.15207,
      soc: 99.40679999999999,
      solar: 2.6971755555555554,
      timestamp: "2025-02-03T15:30:00+11:00",
    },
    {
      battery: 0.8928805555555556,
      ev: 0.0,
      grid: -0.03224944444444444,
      house: 2.224426111111111,
      soc: 97.80104999999999,
      solar: 1.363795,
      timestamp: "2025-02-03T16:00:00+11:00",
    },
    {
      battery: 1.414821111111111,
      ev: 0.0,
      grid: 0.004097222222222223,
      house: 2.176738333333333,
      soc: 93.55793333333332,
      solar: 0.75782,
      timestamp: "2025-02-03T16:30:00+11:00",
    },
    {
      battery: 0.9008238888888889,
      ev: 0.0,
      grid: 0.0024744444444444445,
      house: 1.4375944444444444,
      soc: 89.83211666666666,
      solar: 0.5342961111111111,
      timestamp: "2025-02-03T17:00:00+11:00",
    },
    {
      battery: 1.2046927777777778,
      ev: 0.0,
      grid: 0.0007727777777777778,
      house: 1.5870194444444445,
      soc: 86.52319999999999,
      solar: 0.3815538888888889,
      timestamp: "2025-02-03T17:30:00+11:00",
    },
    {
      battery: 1.3632077777777778,
      ev: 0.0,
      grid: 0.0025711111111111113,
      house: 1.66579,
      soc: 82.0332,
      solar: 0.3000111111111111,
      timestamp: "2025-02-03T18:00:00+11:00",
    },
    {
      battery: 2.474343888888889,
      ev: 0.0,
      grid: -1.039506111111111,
      house: 1.6683394444444444,
      soc: 76.70585,
      solar: 0.23350166666666666,
      timestamp: "2025-02-03T18:30:00+11:00",
    },
    {
      battery: 3.1280366666666666,
      ev: 0.0,
      grid: -1.7364644444444444,
      house: 1.5439305555555556,
      soc: 67.3556,
      solar: 0.15235833333333335,
      timestamp: "2025-02-03T19:00:00+11:00",
    },
    {
      battery: 4.938328888888889,
      ev: 0.0,
      grid: -3.618247222222222,
      house: 1.3670972222222222,
      soc: 51.99358333333333,
      solar: 0.047015555555555553,
      timestamp: "2025-02-03T19:30:00+11:00",
    },
    {
      battery: 4.612055555555555,
      ev: 0.0,
      grid: -3.2329305555555554,
      house: 1.379125,
      soc: 35.55846666666667,
      solar: 0.0,
      timestamp: "2025-02-03T20:00:00+11:00",
    },
    {
      battery: 1.5178755555555556,
      ev: 0.0,
      grid: -0.22468611111111111,
      house: 1.2931894444444445,
      soc: 26.126033333333336,
      solar: 0.0,
      timestamp: "2025-02-03T20:30:00+11:00",
    },
    {
      battery: 1.4062561111111112,
      ev: 0.0,
      grid: -0.28518166666666667,
      house: 1.1210744444444445,
      soc: 21.192766666666667,
      solar: 0.0,
      timestamp: "2025-02-03T21:00:00+11:00",
    },
    {
      battery: 1.023063888888889,
      ev: 0.0,
      grid: 0.0014338888888888888,
      house: 1.0244977777777777,
      soc: 17.959949999999996,
      solar: 0.0,
      timestamp: "2025-02-03T21:30:00+11:00",
    },
    {
      battery: 1.8145816666666668,
      ev: 0.0,
      grid: 0.12137055555555555,
      house: 1.9359522222222223,
      soc: 11.337705000000001,
      solar: 0.0,
      timestamp: "2025-02-03T22:00:00+11:00",
    },
    {
      battery: -0.013786666666666666,
      ev: 0.0,
      grid: 1.9869511111111111,
      house: 1.9731644444444445,
      soc: 9.991436666666667,
      solar: 0.0,
      timestamp: "2025-02-03T22:30:00+11:00",
    },
    {
      battery: -0.014125,
      ev: 0.0,
      grid: 0.6493011111111111,
      house: 0.6351761111111112,
      soc: 10.040316666666667,
      solar: 0.0,
      timestamp: "2025-02-03T23:00:00+11:00",
    },
    {
      battery: -0.014341666666666667,
      ev: 0.0,
      grid: 0.4414633333333333,
      house: 0.4271216666666667,
      soc: 10.089766666666668,
      solar: 0.0,
      timestamp: "2025-02-03T23:30:00+11:00",
    },
    {
      battery: -0.014368333333333334,
      ev: 0.0,
      grid: 0.3919588888888889,
      house: 0.37759055555555554,
      soc: 10.1395,
      solar: 0.0,
      timestamp: "2025-02-04T00:00:00+11:00",
    },
    {
      battery: -0.014518888888888888,
      ev: 0.0,
      grid: 0.45278277777777776,
      house: 0.4382638888888889,
      soc: 10.189983333333332,
      solar: 0.0,
      timestamp: "2025-02-04T00:30:00+11:00",
    },
    {
      battery: -0.014326666666666666,
      ev: 0.0,
      grid: 0.3753977777777778,
      house: 0.3610711111111111,
      soc: 10.240583333333332,
      solar: 0.0,
      timestamp: "2025-02-04T01:00:00+11:00",
    },
    {
      battery: -0.011572777777777777,
      ev: 0.0,
      grid: 0.4032211111111111,
      house: 0.3916483333333333,
      soc: 10.285516666666666,
      solar: 0.0,
      timestamp: "2025-02-04T01:30:00+11:00",
    },
    {
      battery: -0.010565,
      ev: 0.0,
      grid: 0.40719,
      house: 0.396625,
      soc: 10.3238,
      solar: 0.0,
      timestamp: "2025-02-04T02:00:00+11:00",
    },
    {
      battery: -2.7521894444444444,
      ev: 0.0,
      grid: 3.2424694444444446,
      house: 0.49028,
      soc: 17.02405,
      solar: 0.0,
      timestamp: "2025-02-04T02:30:00+11:00",
    },
    {
      battery: -0.998905,
      ev: 0.0,
      grid: 1.3679577777777778,
      house: 0.3690527777777778,
      soc: 22.485233333333337,
      solar: 0.0,
      timestamp: "2025-02-04T03:00:00+11:00",
    },
    {
      battery: -0.007963333333333333,
      ev: 0.0,
      grid: 0.324655,
      house: 0.31669166666666665,
      soc: 22.69113333333333,
      solar: 0.0,
      timestamp: "2025-02-04T03:30:00+11:00",
    },
    {
      battery: -1.432288888888889,
      ev: 0.0,
      grid: 1.7888383333333333,
      house: 0.3565494444444444,
      soc: 26.439983333333334,
      solar: 0.0,
      timestamp: "2025-02-04T04:00:00+11:00",
    },
    {
      battery: -0.007778333333333333,
      ev: 0.0,
      grid: 0.35405888888888887,
      house: 0.34628055555555554,
      soc: 27.386266666666668,
      solar: 0.0,
      timestamp: "2025-02-04T04:30:00+11:00",
    },
    {
      battery: -0.5757661111111111,
      ev: 0.0,
      grid: 0.91787,
      house: 0.3421038888888889,
      soc: 29.460783333333335,
      solar: 0.0,
      timestamp: "2025-02-04T05:00:00+11:00",
    },
    {
      battery: 0.3222911111111111,
      ev: 0.0,
      grid: 0.0019461111111111111,
      house: 0.32423722222222223,
      soc: 28.63801666666667,
      solar: 0.0,
      timestamp: "2025-02-04T05:30:00+11:00",
    },
    {
      battery: 0.4402877777777778,
      ev: 0.0,
      grid: 0.0009516666666666666,
      house: 0.4431833333333333,
      soc: 27.41671666666667,
      solar: 0.0019438888888888889,
      timestamp: "2025-02-04T06:00:00+11:00",
    },
    {
      battery: 1.8106588888888888,
      ev: 0.0,
      grid: -1.425333888888889,
      house: 0.47232277777777776,
      soc: 24.798316666666665,
      solar: 0.08699777777777777,
      timestamp: "2025-02-04T06:30:00+11:00",
    },
    {
      battery: 2.5784438888888888,
      ev: 0.0,
      grid: -2.1514005555555555,
      house: 0.7908338888888888,
      soc: 17.028066666666668,
      solar: 0.36379055555555556,
      timestamp: "2025-02-04T07:00:00+11:00",
    },
    {
      battery: 0.06725722222222222,
      ev: 0.0,
      grid: -0.10789666666666667,
      house: 0.522145,
      soc: 11.454883333333335,
      solar: 0.5627844444444444,
      timestamp: "2025-02-04T07:30:00+11:00",
    },
    {
      battery: 0.44592611111111113,
      ev: 0.0,
      grid: 2.6257522222222223,
      house: 3.8458877777777776,
      soc: 9.992863333333334,
      solar: 0.7742094444444444,
      timestamp: "2025-02-04T08:00:00+11:00",
    },
    {
      battery: -0.013215,
      ev: 0.0,
      grid: 0.5434322222222222,
      house: 1.807271111111111,
      soc: 10.009441666666666,
      solar: 1.277053888888889,
      timestamp: "2025-02-04T08:30:00+11:00",
    },
    {
      battery: -0.2633888888888889,
      ev: 0.0,
      grid: 0.03373944444444445,
      house: 1.6967661111111112,
      soc: 10.29195,
      solar: 1.9264155555555555,
      timestamp: "2025-02-04T09:00:00+11:00",
    },
    {
      battery: -0.45089666666666667,
      ev: 0.0,
      grid: -0.9371038888888888,
      house: 1.746471111111111,
      soc: 11.550366666666667,
      solar: 3.1344716666666668,
      timestamp: "2025-02-04T09:30:00+11:00",
    },
    {
      battery: -3.4554533333333333,
      ev: 0.0,
      grid: -0.007558888888888889,
      house: 1.7885305555555555,
      soc: 18.237183333333334,
      solar: 5.251542777777778,
      timestamp: "2025-02-04T10:00:00+11:00",
    },
    {
      battery: -4.142592777777778,
      ev: 0.0,
      grid: 0.005396666666666667,
      house: 1.852638888888889,
      soc: 31.6331,
      solar: 5.989835,
      timestamp: "2025-02-04T10:30:00+11:00",
    },
    {
      battery: -3.383872777777778,
      ev: 0.0,
      grid: -0.0017966666666666667,
      house: 2.78851,
      soc: 43.41598333333334,
      solar: 6.174179444444444,
      timestamp: "2025-02-04T11:00:00+11:00",
    },
    {
      battery: -3.6546794444444446,
      ev: 0.0,
      grid: 0.0009605555555555555,
      house: 2.8121038888888887,
      soc: 54.7809,
      solar: 6.4658227777777775,
      timestamp: "2025-02-04T11:30:00+11:00",
    },
    {
      battery: -3.9076155555555556,
      ev: 0.0,
      grid: 4.888888888888889e-5,
      house: 2.8351538888888888,
      soc: 67.17951666666666,
      solar: 6.742720555555556,
      timestamp: "2025-02-04T12:00:00+11:00",
    },
    {
      battery: -4.228051111111111,
      ev: 0.0,
      grid: -0.1806038888888889,
      house: 2.2078016666666667,
      soc: 79.97441666666667,
      solar: 6.616456666666667,
      timestamp: "2025-02-04T12:30:00+11:00",
    },
    {
      battery: -4.273698333333333,
      ev: 0.0,
      grid: -0.26186055555555554,
      house: 1.9320544444444445,
      soc: 93.49136666666668,
      solar: 6.467613333333333,
      timestamp: "2025-02-04T13:00:00+11:00",
    },
    {
      battery: -0.99917,
      ev: 0.0,
      grid: -2.4490761111111112,
      house: 1.83926,
      soc: 100.0,
      solar: 5.287506111111111,
      timestamp: "2025-02-04T13:30:00+11:00",
    },
    {
      battery: 0.08810888888888889,
      ev: 0.0,
      grid: -2.660721111111111,
      house: 1.8975016666666666,
      soc: 99.931,
      solar: 4.470113888888889,
      timestamp: "2025-02-04T14:00:00+11:00",
    },
    {
      battery: -0.003821111111111111,
      ev: 0.0,
      grid: -2.059832777777778,
      house: 1.9240416666666667,
      soc: 99.68641666666667,
      solar: 3.9876955555555558,
      timestamp: "2025-02-04T14:30:00+11:00",
    },
    {
      battery: -0.01169,
      ev: 0.0,
      grid: -1.5417538888888889,
      house: 1.7877444444444444,
      soc: 99.72525,
      solar: 3.341188333333333,
      timestamp: "2025-02-04T15:00:00+11:00",
    },
    {
      battery: -0.012310555555555555,
      ev: 0.0,
      grid: -1.0977594444444445,
      house: 1.7007672222222223,
      soc: 99.76556666666666,
      solar: 2.810837222222222,
      timestamp: "2025-02-04T15:30:00+11:00",
    },
    {
      battery: 0.15943333333333334,
      ev: 0.0,
      grid: -0.143015,
      house: 1.6396644444444444,
      soc: 99.62155,
      solar: 1.6232461111111112,
      timestamp: "2025-02-04T16:00:00+11:00",
    },
    {
      battery: 0.5247483333333334,
      ev: 0.0,
      grid: 0.0014761111111111112,
      house: 1.6280144444444444,
      soc: 98.1368,
      solar: 1.10179,
      timestamp: "2025-02-04T16:30:00+11:00",
    },
    {
      battery: 0.6545861111111111,
      ev: 0.0,
      grid: 0.0023794444444444444,
      house: 1.5758322222222223,
      soc: 96.44648333333333,
      solar: 0.9188666666666667,
      timestamp: "2025-02-04T17:00:00+11:00",
    },
    {
      battery: 0.8059366666666666,
      ev: 0.0,
      grid: 0.0014005555555555555,
      house: 1.4418322222222222,
      soc: 93.91736666666667,
      solar: 0.634495,
      timestamp: "2025-02-04T17:30:00+11:00",
    },
    {
      battery: 0.6781738888888889,
      ev: 0.0,
      grid: 0.004402777777777778,
      house: 1.556105,
      soc: 91.54951666666666,
      solar: 0.8735283333333334,
      timestamp: "2025-02-04T18:00:00+11:00",
    },
    {
      battery: 1.7843816666666668,
      ev: 0.0,
      grid: -0.5077116666666667,
      house: 1.683825,
      soc: 87.82913333333335,
      solar: 0.407155,
      timestamp: "2025-02-04T18:30:00+11:00",
    },
    {
      battery: 4.8402272222222225,
      ev: 0.0,
      grid: -3.321466111111111,
      house: 1.698341111111111,
      soc: 75.37465,
      solar: 0.17958,
      timestamp: "2025-02-04T19:00:00+11:00",
    },
    {
      battery: 4.938086666666667,
      ev: 0.0,
      grid: -3.201415,
      house: 1.7950561111111112,
      soc: 58.9545,
      solar: 0.05838444444444445,
      timestamp: "2025-02-04T19:30:00+11:00",
    },
    {
      battery: 4.932531111111111,
      ev: 0.0,
      grid: -3.422073888888889,
      house: 1.5104572222222221,
      soc: 42.355,
      solar: 0.0,
      timestamp: "2025-02-04T20:00:00+11:00",
    },
    {
      battery: 4.885156111111111,
      ev: 0.0,
      grid: -3.4266288888888887,
      house: 1.4585272222222223,
      soc: 25.755983333333333,
      solar: 0.0,
      timestamp: "2025-02-04T20:30:00+11:00",
    },
    {
      battery: 1.3635466666666667,
      ev: 0.0,
      grid: -0.0646661111111111,
      house: 1.2988805555555556,
      soc: 15.993949999999998,
      solar: 0.0,
      timestamp: "2025-02-04T21:00:00+11:00",
    },
    {
      battery: 0.427455,
      ev: 0.0,
      grid: 0.8966044444444444,
      house: 1.3240594444444445,
      soc: 13.83191666666667,
      solar: 0.0,
      timestamp: "2025-02-04T21:30:00+11:00",
    },
    {
      battery: 0.8165983333333333,
      ev: 0.0,
      grid: 0.22856333333333334,
      house: 1.0451616666666668,
      soc: 11.404288333333334,
      solar: 0.0,
      timestamp: "2025-02-04T22:00:00+11:00",
    },
    {
      battery: -0.013294444444444444,
      ev: 0.0,
      grid: 1.9022605555555556,
      house: 1.8889661111111111,
      soc: 9.978831666666666,
      solar: 0.0,
      timestamp: "2025-02-04T22:30:00+11:00",
    },
    {
      battery: -0.013635555555555555,
      ev: 0.0,
      grid: 1.0174444444444444,
      house: 1.003808888888889,
      soc: 10.026066666666667,
      solar: 0.0,
      timestamp: "2025-02-04T23:00:00+11:00",
    },
    {
      battery: -0.013804444444444444,
      ev: 0.0,
      grid: 0.4724461111111111,
      house: 0.45864166666666667,
      soc: 10.073866666666667,
      solar: 0.0,
      timestamp: "2025-02-04T23:30:00+11:00",
    },
    {
      battery: -0.013951666666666666,
      ev: 0.0,
      grid: 0.38458777777777775,
      house: 0.3706361111111111,
      soc: 10.1224,
      solar: 0.0,
      timestamp: "2025-02-05T00:00:00+11:00",
    },
    {
      battery: -0.014096666666666667,
      ev: 0.0,
      grid: 0.2610766666666667,
      house: 0.24698,
      soc: 10.171516666666667,
      solar: 0.0,
      timestamp: "2025-02-05T00:30:00+11:00",
    },
    {
      battery: -0.01411388888888889,
      ev: 0.0,
      grid: 0.27606555555555556,
      house: 0.2619516666666667,
      soc: 10.220883333333333,
      solar: 0.0,
      timestamp: "2025-02-05T01:00:00+11:00",
    },
    {
      battery: -1.5239333333333334,
      ev: 0.0,
      grid: 1.8476555555555556,
      house: 0.32372222222222224,
      soc: 13.544000000000002,
      solar: 0.0,
      timestamp: "2025-02-05T01:30:00+11:00",
    },
    {
      battery: -0.014165,
      ev: 0.0,
      grid: 0.4615522222222222,
      house: 0.4473872222222222,
      soc: 15.268366666666667,
      solar: 0.0,
      timestamp: "2025-02-05T02:00:00+11:00",
    },
    {
      battery: -0.014419444444444445,
      ev: 0.0,
      grid: 0.5517205555555555,
      house: 0.5373011111111111,
      soc: 15.317950000000002,
      solar: 0.0,
      timestamp: "2025-02-05T02:30:00+11:00",
    },
    {
      battery: 0.05975166666666667,
      ev: 0.0,
      grid: 0.4512366666666667,
      house: 0.5109883333333334,
      soc: 15.121616666666666,
      solar: 0.0,
      timestamp: "2025-02-05T03:00:00+11:00",
    },
    {
      battery: -0.0117,
      ev: 0.0,
      grid: 0.36978777777777777,
      house: 0.3580877777777778,
      soc: 15.161783333333334,
      solar: 0.0,
      timestamp: "2025-02-05T03:30:00+11:00",
    },
    {
      battery: -0.010548888888888889,
      ev: 0.0,
      grid: 0.35862666666666665,
      house: 0.34807777777777776,
      soc: 15.199966666666667,
      solar: 0.0,
      timestamp: "2025-02-05T04:00:00+11:00",
    },
    {
      battery: -0.010030555555555555,
      ev: 0.0,
      grid: 0.33145444444444444,
      house: 0.3214238888888889,
      soc: 15.2359,
      solar: 0.0,
      timestamp: "2025-02-05T04:30:00+11:00",
    },
    {
      battery: -0.009739444444444445,
      ev: 0.0,
      grid: 0.38504944444444444,
      house: 0.37531,
      soc: 15.2705,
      solar: 0.0,
      timestamp: "2025-02-05T05:00:00+11:00",
    },
    {
      battery: 0.2807738888888889,
      ev: 0.0,
      grid: 0.06734055555555556,
      house: 0.34811444444444445,
      soc: 14.813866666666668,
      solar: 0.0,
      timestamp: "2025-02-05T05:30:00+11:00",
    },
    {
      battery: 0.32412111111111114,
      ev: 0.0,
      grid: 0.0026505555555555557,
      house: 0.33034833333333335,
      soc: 13.669850000000002,
      solar: 0.003576666666666667,
      timestamp: "2025-02-05T06:00:00+11:00",
    },
    {
      battery: 0.09607222222222223,
      ev: 0.0,
      grid: 0.05008888888888889,
      house: 0.2934555555555556,
      soc: 12.974733333333333,
      solar: 0.14729444444444445,
      timestamp: "2025-02-05T06:30:00+11:00",
    },
    {
      battery: 0.5942788888888889,
      ev: 0.0,
      grid: 0.0008116666666666667,
      house: 0.9240688888888889,
      soc: 11.671450000000002,
      solar: 0.3289783333333333,
      timestamp: "2025-02-05T07:00:00+11:00",
    },
    {
      battery: 0.13273222222222222,
      ev: 0.0,
      grid: 0.11768,
      house: 0.7319577777777778,
      soc: 10.691816666666668,
      solar: 0.48154555555555556,
      timestamp: "2025-02-05T07:30:00+11:00",
    },
    {
      battery: -2.5646,
      ev: 0.0,
      grid: 2.247716666666667,
      house: 0.512665,
      soc: 13.491766666666665,
      solar: 0.8295483333333333,
      timestamp: "2025-02-05T08:00:00+11:00",
    },
    {
      battery: -3.0796455555555555,
      ev: 0.0,
      grid: 2.3056277777777776,
      house: 0.3971794444444444,
      soc: 26.243283333333334,
      solar: 1.1711972222222222,
      timestamp: "2025-02-05T08:30:00+11:00",
    },
    {
      battery: -1.2891688888888888,
      ev: 0.0,
      grid: -0.004358888888888889,
      house: 0.3244033333333333,
      soc: 31.1225,
      solar: 1.6179311111111112,
      timestamp: "2025-02-05T09:00:00+11:00",
    },
    {
      battery: -2.0318622222222222,
      ev: 0.0,
      grid: -0.004807777777777778,
      house: 0.33102944444444443,
      soc: 36.39033333333334,
      solar: 2.3676994444444444,
      timestamp: "2025-02-05T09:30:00+11:00",
    },
    {
      battery: -4.147110555555556,
      ev: 0.0,
      grid: -0.0002933333333333333,
      house: 0.26405388888888887,
      soc: 46.87591666666666,
      solar: 4.411457777777778,
      timestamp: "2025-02-05T10:00:00+11:00",
    },
    {
      battery: -5.129924444444445,
      ev: 0.0,
      grid: 0.0,
      house: 0.3135511111111111,
      soc: 62.46458333333333,
      solar: 5.443475555555556,
      timestamp: "2025-02-05T10:30:00+11:00",
    },
    {
      battery: -4.80328,
      ev: 0.0,
      grid: -0.022814444444444443,
      house: 0.3430011111111111,
      soc: 78.84276666666666,
      solar: 5.169095555555556,
      timestamp: "2025-02-05T11:00:00+11:00",
    },
    {
      battery: -0.7657572222222222,
      ev: 0.0,
      grid: -3.881762222222222,
      house: 0.4166388888888889,
      soc: 85.86433333333332,
      solar: 5.064158333333333,
      timestamp: "2025-02-05T11:30:00+11:00",
    },
    {
      battery: -0.009496666666666667,
      ev: 0.0,
      grid: -4.677704444444444,
      house: 0.44686944444444443,
      soc: 87.14258333333333,
      solar: 5.134070555555556,
      timestamp: "2025-02-05T12:00:00+11:00",
    },
    {
      battery: -1.7305794444444444,
      ev: 0.0,
      grid: -3.4962605555555557,
      house: 0.44755388888888886,
      soc: 89.31235,
      solar: 5.674393888888889,
      timestamp: "2025-02-05T12:30:00+11:00",
    },
    {
      battery: -0.009822222222222222,
      ev: 0.0,
      grid: -4.156611666666667,
      house: 0.3576505555555556,
      soc: 92.68545000000002,
      solar: 4.524084444444444,
      timestamp: "2025-02-05T13:00:00+11:00",
    },
    {
      battery: -0.011253333333333334,
      ev: 0.0,
      grid: -2.1233983333333333,
      house: 0.333145,
      soc: 92.72231666666666,
      solar: 2.4677966666666666,
      timestamp: "2025-02-05T13:30:00+11:00",
    },
    {
      battery: -0.01169111111111111,
      ev: 0.0,
      grid: -3.7523905555555555,
      house: 0.38387166666666667,
      soc: 92.76203333333332,
      solar: 4.147953333333334,
      timestamp: "2025-02-05T14:00:00+11:00",
    },
    {
      battery: -0.011701111111111112,
      ev: 0.0,
      grid: -2.0433855555555556,
      house: 0.32981055555555555,
      soc: 92.8016,
      solar: 2.384897222222222,
      timestamp: "2025-02-05T14:30:00+11:00",
    },
    {
      battery: -0.012445555555555555,
      ev: 0.0,
      grid: -1.0586922222222221,
      house: 0.2783161111111111,
      soc: 92.84228333333334,
      solar: 1.349453888888889,
      timestamp: "2025-02-05T15:00:00+11:00",
    },
    {
      battery: -0.01313888888888889,
      ev: 0.0,
      grid: -0.7912777777777777,
      house: 0.34495777777777775,
      soc: 92.88463333333334,
      solar: 1.1493744444444445,
      timestamp: "2025-02-05T15:30:00+11:00",
    },
    {
      battery: -0.01308388888888889,
      ev: 0.0,
      grid: -0.08145555555555556,
      house: 1.3227116666666667,
      soc: 92.92711666666666,
      solar: 1.417251111111111,
      timestamp: "2025-02-05T16:00:00+11:00",
    },
    {
      battery: 0.8120944444444445,
      ev: 0.0,
      grid: 0.012163333333333333,
      house: 1.410938888888889,
      soc: 91.68353333333333,
      solar: 0.5866811111111111,
      timestamp: "2025-02-05T16:30:00+11:00",
    },
    {
      battery: 0.8239838888888888,
      ev: 0.0,
      grid: 0.007204444444444445,
      house: 1.4703294444444444,
      soc: 88.76958333333333,
      solar: 0.6391411111111112,
      timestamp: "2025-02-05T17:00:00+11:00",
    },
    {
      battery: 1.7318827777777779,
      ev: 0.0,
      grid: 0.003651111111111111,
      house: 2.3668144444444446,
      soc: 85.08431666666667,
      solar: 0.6312805555555555,
      timestamp: "2025-02-05T17:30:00+11:00",
    },
    {
      battery: 4.345867222222222,
      ev: 0.0,
      grid: -2.151502777777778,
      house: 2.4622633333333335,
      soc: 73.80585,
      solar: 0.2678988888888889,
      timestamp: "2025-02-05T18:00:00+11:00",
    },
    {
      battery: 4.880913888888889,
      ev: 0.0,
      grid: -2.9696783333333334,
      house: 2.0456716666666668,
      soc: 57.92765,
      solar: 0.13443611111111112,
      timestamp: "2025-02-05T18:30:00+11:00",
    },
    {
      battery: 1.9642172222222223,
      ev: 0.0,
      grid: -0.05512388888888889,
      house: 1.9522355555555555,
      soc: 47.116283333333335,
      solar: 0.04314222222222222,
      timestamp: "2025-02-05T19:00:00+11:00",
    },
    {
      battery: 1.839096111111111,
      ev: 0.0,
      grid: -0.0033577777777777777,
      house: 1.838773888888889,
      soc: 40.89355,
      solar: 0.0030355555555555557,
      timestamp: "2025-02-05T19:30:00+11:00",
    },
    {
      battery: 1.5048494444444445,
      ev: 0.0,
      grid: 0.002348888888888889,
      house: 1.5071983333333334,
      soc: 35.47321666666667,
      solar: 0.0,
      timestamp: "2025-02-05T20:00:00+11:00",
    },
    {
      battery: 1.4461883333333334,
      ev: 0.0,
      grid: 0.002072777777777778,
      house: 1.4482611111111112,
      soc: 30.486316666666667,
      solar: 0.0,
      timestamp: "2025-02-05T20:30:00+11:00",
    },
    {
      battery: 1.6776616666666666,
      ev: 0.0,
      grid: 0.319005,
      house: 1.9966666666666666,
      soc: 26.21865,
      solar: 0.0,
      timestamp: "2025-02-05T21:00:00+11:00",
    },
    {
      battery: 2.277792777777778,
      ev: 0.0,
      grid: 0.0017144444444444444,
      house: 2.279507222222222,
      soc: 18.475383333333333,
      solar: 0.0,
      timestamp: "2025-02-05T21:30:00+11:00",
    },
    {
      battery: 0.7168483333333333,
      ev: 0.0,
      grid: 0.5354194444444444,
      house: 1.2522677777777778,
      soc: 13.12655,
      solar: 0.0,
      timestamp: "2025-02-05T22:00:00+11:00",
    },
    {
      battery: 0.0003838888888888889,
      ev: 0.0,
      grid: 0.7085861111111111,
      house: 0.70897,
      soc: 12.635566666666668,
      solar: 0.0,
      timestamp: "2025-02-05T22:30:00+11:00",
    },
    {
      battery: -0.014150555555555555,
      ev: 0.0,
      grid: 0.323285,
      house: 0.30913444444444443,
      soc: 12.684566666666667,
      solar: 0.0,
      timestamp: "2025-02-05T23:00:00+11:00",
    },
    {
      battery: -0.014342222222222221,
      ev: 0.0,
      grid: 0.2942022222222222,
      house: 0.27986,
      soc: 12.734333333333332,
      solar: 0.0,
      timestamp: "2025-02-05T23:30:00+11:00",
    },
    {
      battery: -1.4050444444444445,
      ev: 0.0,
      grid: 1.8240783333333332,
      house: 0.4190338888888889,
      soc: 16.382066666666663,
      solar: 0.0,
      timestamp: "2025-02-06T00:00:00+11:00",
    },
    {
      battery: 0.23899055555555557,
      ev: 0.0,
      grid: 0.2554222222222222,
      house: 0.49441277777777776,
      soc: 16.82695,
      solar: 0.0,
      timestamp: "2025-02-06T00:30:00+11:00",
    },
    {
      battery: -0.01251,
      ev: 0.0,
      grid: 0.4400072222222222,
      house: 0.4274972222222222,
      soc: 16.5862,
      solar: 0.0,
      timestamp: "2025-02-06T01:00:00+11:00",
    },
    {
      battery: -0.010624444444444445,
      ev: 0.0,
      grid: 0.37425444444444445,
      house: 0.36363,
      soc: 16.625833333333333,
      solar: 0.0,
      timestamp: "2025-02-06T01:30:00+11:00",
    },
    {
      battery: -0.010026666666666666,
      ev: 0.0,
      grid: 0.38301722222222223,
      house: 0.37299055555555555,
      soc: 16.6628,
      solar: 0.0,
      timestamp: "2025-02-06T02:00:00+11:00",
    },
    {
      battery: -0.009647777777777778,
      ev: 0.0,
      grid: 0.3134661111111111,
      house: 0.30381833333333336,
      soc: 16.6978,
      solar: 0.0,
      timestamp: "2025-02-06T02:30:00+11:00",
    },
    {
      battery: -0.009512777777777778,
      ev: 0.0,
      grid: 0.31376055555555554,
      house: 0.3042477777777778,
      soc: 16.731816666666663,
      solar: 0.0,
      timestamp: "2025-02-06T03:00:00+11:00",
    },
    {
      battery: -0.009426666666666667,
      ev: 0.0,
      grid: 0.32689833333333335,
      house: 0.31747166666666665,
      soc: 16.765516666666667,
      solar: 0.0,
      timestamp: "2025-02-06T03:30:00+11:00",
    },
    {
      battery: 0.04444222222222222,
      ev: 0.0,
      grid: 0.28420833333333334,
      house: 0.32865055555555556,
      soc: 16.6522,
      solar: 0.0,
      timestamp: "2025-02-06T04:00:00+11:00",
    },
    {
      battery: -0.009291111111111111,
      ev: 0.0,
      grid: 0.2923294444444444,
      house: 0.28303833333333334,
      soc: 16.652883333333335,
      solar: 0.0,
      timestamp: "2025-02-06T04:30:00+11:00",
    },
    {
      battery: -0.009200555555555556,
      ev: 0.0,
      grid: 0.33173555555555556,
      house: 0.322535,
      soc: 16.685566666666666,
      solar: 0.0,
      timestamp: "2025-02-06T05:00:00+11:00",
    },
    {
      battery: -0.009275555555555556,
      ev: 0.0,
      grid: 0.24909444444444445,
      house: 0.2398188888888889,
      soc: 16.71885,
      solar: 0.0,
      timestamp: "2025-02-06T05:30:00+11:00",
    },
    {
      battery: -0.009129444444444445,
      ev: 0.0,
      grid: 0.24380944444444444,
      house: 0.2356572222222222,
      soc: 16.751883333333335,
      solar: 0.0009772222222222222,
      timestamp: "2025-02-06T06:00:00+11:00",
    },
    {
      battery: 0.06111888888888889,
      ev: 0.0,
      grid: 0.498855,
      house: 0.6883066666666666,
      soc: 16.57381666666667,
      solar: 0.12833277777777777,
      timestamp: "2025-02-06T06:30:00+11:00",
    },
    {
      battery: 0.12300833333333333,
      ev: 0.0,
      grid: 0.08549277777777778,
      house: 0.5278005555555556,
      soc: 16.322599999999998,
      solar: 0.31929944444444447,
      timestamp: "2025-02-06T07:00:00+11:00",
    },
    {
      battery: 0.019841666666666667,
      ev: 0.0,
      grid: 0.08336166666666667,
      house: 0.6155794444444445,
      soc: 16.086250000000003,
      solar: 0.5123761111111111,
      timestamp: "2025-02-06T07:30:00+11:00",
    },
    {
      battery: -0.31924555555555556,
      ev: 0.0,
      grid: -0.049815,
      house: 0.44232333333333335,
      soc: 16.555466666666664,
      solar: 0.8113838888888889,
      timestamp: "2025-02-06T08:00:00+11:00",
    },
    {
      battery: -0.8279866666666666,
      ev: 0.0,
      grid: -0.0027133333333333332,
      house: 0.37316444444444447,
      soc: 18.6019,
      solar: 1.2038644444444444,
      timestamp: "2025-02-06T08:30:00+11:00",
    },
    {
      battery: -1.5421083333333334,
      ev: 0.0,
      grid: -0.004250555555555555,
      house: 0.3941516666666667,
      soc: 22.381983333333334,
      solar: 1.9405105555555555,
      timestamp: "2025-02-06T09:00:00+11:00",
    },
    {
      battery: -3.44185,
      ev: 0.0,
      grid: -0.002258888888888889,
      house: 0.46575833333333333,
      soc: 31.24631666666667,
      solar: 3.909867222222222,
      timestamp: "2025-02-06T09:30:00+11:00",
    },
    {
      battery: -4.436246111111111,
      ev: 0.0,
      grid: -0.0052161111111111115,
      house: 0.48498277777777776,
      soc: 44.18533333333334,
      solar: 4.926445,
      timestamp: "2025-02-06T10:00:00+11:00",
    },
    {
      battery: -4.994415555555555,
      ev: 0.0,
      grid: -0.1724,
      house: 0.498955,
      soc: 59.71241666666666,
      solar: 5.6657705555555555,
      timestamp: "2025-02-06T10:30:00+11:00",
    },
    {
      battery: -4.949103888888889,
      ev: 0.0,
      grid: -0.5996205555555556,
      house: 0.4802388888888889,
      soc: 75.67219999999999,
      solar: 6.0289633333333335,
      timestamp: "2025-02-06T11:00:00+11:00",
    },
    {
      battery: -4.493432777777778,
      ev: 0.0,
      grid: -0.8299355555555555,
      house: 0.4771444444444444,
      soc: 90.86465,
      solar: 5.800512777777778,
      timestamp: "2025-02-06T11:30:00+11:00",
    },
    {
      battery: -0.10722555555555556,
      ev: 0.0,
      grid: -4.497255555555555,
      house: 0.40595555555555557,
      soc: 96.85878333333334,
      solar: 5.010436666666667,
      timestamp: "2025-02-06T12:00:00+11:00",
    },
    {
      battery: -0.01083611111111111,
      ev: 0.0,
      grid: -4.119850555555556,
      house: 0.3595322222222222,
      soc: 96.89553333333333,
      solar: 4.490218888888889,
      timestamp: "2025-02-06T12:30:00+11:00",
    },
    {
      battery: -0.011392222222222222,
      ev: 0.0,
      grid: -4.0601344444444445,
      house: 0.35479333333333335,
      soc: 96.93398333333334,
      solar: 4.42632,
      timestamp: "2025-02-06T13:00:00+11:00",
    },
    {
      battery: -0.011791666666666667,
      ev: 0.0,
      grid: -4.413558888888889,
      house: 0.37343444444444446,
      soc: 96.97366666666666,
      solar: 4.798785,
      timestamp: "2025-02-06T13:30:00+11:00",
    },
    {
      battery: -0.4135316666666667,
      ev: 0.0,
      grid: -3.472681666666667,
      house: 0.5326683333333333,
      soc: 97.22413333333334,
      solar: 4.418881666666667,
      timestamp: "2025-02-06T14:00:00+11:00",
    },
    {
      battery: -0.060996666666666664,
      ev: 0.0,
      grid: -2.400857222222222,
      house: 1.4810094444444444,
      soc: 98.48523333333333,
      solar: 3.9428633333333334,
      timestamp: "2025-02-06T14:30:00+11:00",
    },
    {
      battery: -0.0131,
      ev: 0.0,
      grid: -1.7645994444444444,
      house: 1.4362677777777777,
      soc: 98.52758333333333,
      solar: 3.213967222222222,
      timestamp: "2025-02-06T15:00:00+11:00",
    },
    {
      battery: -0.013495555555555556,
      ev: 0.0,
      grid: -0.9691605555555556,
      house: 1.4779727777777778,
      soc: 98.57123333333332,
      solar: 2.460628888888889,
      timestamp: "2025-02-06T15:30:00+11:00",
    },
    {
      battery: 2.386671111111111,
      ev: 0.0,
      grid: -2.173808333333333,
      house: 1.4545722222222222,
      soc: 94.25349999999999,
      solar: 1.2417094444444445,
      timestamp: "2025-02-06T16:00:00+11:00",
    },
    {
      battery: 0.6726344444444444,
      ev: 0.0,
      grid: 0.002033888888888889,
      house: 1.5284594444444444,
      soc: 89.60873333333332,
      solar: 0.8537911111111111,
      timestamp: "2025-02-06T16:30:00+11:00",
    },
    {
      battery: 4.035329444444445,
      ev: 0.0,
      grid: -2.9932266666666667,
      house: 1.7641272222222222,
      soc: 80.89385,
      solar: 0.7220244444444445,
      timestamp: "2025-02-06T17:00:00+11:00",
    },
    {
      battery: 2.9853616666666665,
      ev: 0.0,
      grid: -1.83834,
      house: 1.5551166666666667,
      soc: 68.90481666666668,
      solar: 0.408095,
      timestamp: "2025-02-06T17:30:00+11:00",
    },
    {
      battery: 3.0414844444444444,
      ev: 0.0,
      grid: -1.32811,
      house: 2.0125027777777778,
      soc: 59.855050000000006,
      solar: 0.29912833333333333,
      timestamp: "2025-02-06T18:00:00+11:00",
    },
    {
      battery: 1.455978888888889,
      ev: 0.0,
      grid: 0.002077222222222222,
      house: 1.6844683333333332,
      soc: 52.168933333333335,
      solar: 0.2264122222222222,
      timestamp: "2025-02-06T18:30:00+11:00",
    },
    {
      battery: 2.0695155555555558,
      ev: 0.0,
      grid: -0.5491061111111111,
      house: 1.6621416666666666,
      soc: 45.432716666666664,
      solar: 0.14173222222222223,
      timestamp: "2025-02-06T19:00:00+11:00",
    },
    {
      battery: 3.443887222222222,
      ev: 0.0,
      grid: -2.218933888888889,
      house: 1.2647111111111111,
      soc: 36.58056666666667,
      solar: 0.03975777777777778,
      timestamp: "2025-02-06T19:30:00+11:00",
    },
    {
      battery: 2.8054377777777777,
      ev: 0.0,
      grid: -1.756796111111111,
      house: 1.0486416666666667,
      soc: 27.607216666666663,
      solar: 0.0,
      timestamp: "2025-02-06T20:00:00+11:00",
    },
    {
      battery: 1.3108072222222222,
      ev: 0.0,
      grid: -0.28147222222222223,
      house: 1.029335,
      soc: 19.775199999999998,
      solar: 0.0,
      timestamp: "2025-02-06T20:30:00+11:00",
    },
    {
      battery: 1.0293316666666668,
      ev: 0.0,
      grid: -0.050172777777777774,
      house: 0.9791588888888889,
      soc: 15.596950000000001,
      solar: 0.0,
      timestamp: "2025-02-06T21:00:00+11:00",
    },
    {
      battery: 1.2218538888888888,
      ev: 0.0,
      grid: 0.10124777777777778,
      house: 1.3231016666666666,
      soc: 11.666236666666665,
      solar: 0.0,
      timestamp: "2025-02-06T21:30:00+11:00",
    },
    {
      battery: -0.013590555555555555,
      ev: 0.0,
      grid: 1.1035227777777779,
      house: 1.0899322222222223,
      soc: 10.017756666666665,
      solar: 0.0,
      timestamp: "2025-02-06T22:00:00+11:00",
    },
    {
      battery: -0.013882777777777779,
      ev: 0.0,
      grid: 0.9037261111111111,
      house: 0.8898433333333333,
      soc: 10.06565,
      solar: 0.0,
      timestamp: "2025-02-06T22:30:00+11:00",
    },
    {
      battery: -0.013942777777777778,
      ev: 0.0,
      grid: 0.7073194444444444,
      house: 0.6933766666666666,
      soc: 10.114333333333333,
      solar: 0.0,
      timestamp: "2025-02-06T23:00:00+11:00",
    },
    {
      battery: -0.014177222222222223,
      ev: 0.0,
      grid: 0.5668472222222222,
      house: 0.55267,
      soc: 10.163766666666668,
      solar: 0.0,
      timestamp: "2025-02-06T23:30:00+11:00",
    },
    {
      battery: -0.014221111111111111,
      ev: 0.0,
      grid: 0.34816166666666665,
      house: 0.3339405555555556,
      soc: 10.21348333333333,
      solar: 0.0,
      timestamp: "2025-02-07T00:00:00+11:00",
    },
    {
      battery: -0.013662777777777777,
      ev: 0.0,
      grid: 0.21178,
      house: 0.19811722222222222,
      soc: 10.263266666666667,
      solar: 0.0,
      timestamp: "2025-02-07T00:30:00+11:00",
    },
    {
      battery: -0.011031666666666667,
      ev: 0.0,
      grid: 0.21856111111111112,
      house: 0.20752944444444443,
      soc: 10.305533333333335,
      solar: 0.0,
      timestamp: "2025-02-07T01:00:00+11:00",
    },
    {
      battery: -0.010344444444444444,
      ev: 0.0,
      grid: 0.21874555555555555,
      house: 0.20840111111111112,
      soc: 10.342816666666666,
      solar: 0.0,
      timestamp: "2025-02-07T01:30:00+11:00",
    },
    {
      battery: -0.009939444444444445,
      ev: 0.0,
      grid: 0.20726333333333333,
      house: 0.19732388888888888,
      soc: 10.377983333333335,
      solar: 0.0,
      timestamp: "2025-02-07T02:00:00+11:00",
    },
    {
      battery: -0.009626111111111111,
      ev: 0.0,
      grid: 0.24538833333333335,
      house: 0.23576222222222223,
      soc: 10.412233333333333,
      solar: 0.0,
      timestamp: "2025-02-07T02:30:00+11:00",
    },
    {
      battery: -0.00937,
      ev: 0.0,
      grid: 0.2204127777777778,
      house: 0.21104277777777777,
      soc: 10.4456,
      solar: 0.0,
      timestamp: "2025-02-07T03:00:00+11:00",
    },
    {
      battery: -0.009260555555555555,
      ev: 0.0,
      grid: 0.4663638888888889,
      house: 0.4571033333333333,
      soc: 10.478166666666667,
      solar: 0.0,
      timestamp: "2025-02-07T03:30:00+11:00",
    },
    {
      battery: -0.009207222222222223,
      ev: 0.0,
      grid: 0.4048277777777778,
      house: 0.39562055555555553,
      soc: 10.510666666666667,
      solar: 0.0,
      timestamp: "2025-02-07T04:00:00+11:00",
    },
    {
      battery: -0.009133333333333334,
      ev: 0.0,
      grid: 0.2118211111111111,
      house: 0.20268777777777777,
      soc: 10.542666666666667,
      solar: 0.0,
      timestamp: "2025-02-07T04:30:00+11:00",
    },
    {
      battery: -0.00911,
      ev: 0.0,
      grid: 0.23129388888888888,
      house: 0.22218388888888888,
      soc: 10.574233333333334,
      solar: 0.0,
      timestamp: "2025-02-07T05:00:00+11:00",
    },
    {
      battery: -0.009065555555555556,
      ev: 0.0,
      grid: 0.24585444444444443,
      house: 0.23678888888888888,
      soc: 10.6055,
      solar: 0.0,
      timestamp: "2025-02-07T05:30:00+11:00",
    },
    {
      battery: -0.008935555555555556,
      ev: 0.0,
      grid: 0.17778388888888888,
      house: 0.1700888888888889,
      soc: 10.63635,
      solar: 0.0012405555555555555,
      timestamp: "2025-02-07T06:00:00+11:00",
    },
    {
      battery: -0.008697222222222223,
      ev: 0.0,
      grid: 0.11181166666666667,
      house: 0.2340888888888889,
      soc: 10.666683333333333,
      solar: 0.13097444444444445,
      timestamp: "2025-02-07T06:30:00+11:00",
    },
    {
      battery: -0.008553333333333333,
      ev: 0.0,
      grid: 0.10306944444444445,
      house: 0.41037833333333334,
      soc: 10.695616666666666,
      solar: 0.3158622222222222,
      timestamp: "2025-02-07T07:00:00+11:00",
    },
    {
      battery: -0.008302777777777777,
      ev: 0.0,
      grid: -0.04059388888888889,
      house: 0.4089461111111111,
      soc: 10.723333333333334,
      solar: 0.45784277777777777,
      timestamp: "2025-02-07T07:30:00+11:00",
    },
    {
      battery: -0.007991666666666666,
      ev: 0.0,
      grid: -0.5192666666666667,
      house: 0.24063055555555554,
      soc: 10.749616666666668,
      solar: 0.7678888888888888,
      timestamp: "2025-02-07T08:00:00+11:00",
    },
    {
      battery: -0.008712222222222222,
      ev: 0.0,
      grid: -0.9069388888888889,
      house: 0.25275555555555557,
      soc: 10.774516666666665,
      solar: 1.1684066666666666,
      timestamp: "2025-02-07T08:30:00+11:00",
    },
    {
      battery: -0.8748044444444445,
      ev: 0.0,
      grid: -0.6319288888888889,
      house: 0.2919277777777778,
      soc: 11.63265,
      solar: 1.798661111111111,
      timestamp: "2025-02-07T09:00:00+11:00",
    },
    {
      battery: -3.010937222222222,
      ev: 0.0,
      grid: -0.5903288888888889,
      house: 0.3875277777777778,
      soc: 19.121950000000002,
      solar: 3.988793888888889,
      timestamp: "2025-02-07T09:30:00+11:00",
    },
    {
      battery: -2.41156,
      ev: 0.0,
      grid: -0.535335,
      house: 1.8234344444444444,
      soc: 27.007599999999996,
      solar: 4.770329444444444,
      timestamp: "2025-02-07T10:00:00+11:00",
    },
    {
      battery: -4.442007222222222,
      ev: 0.0,
      grid: -0.3021888888888889,
      house: 1.0183972222222222,
      soc: 39.29483333333334,
      solar: 5.762593333333333,
      timestamp: "2025-02-07T10:30:00+11:00",
    },
    {
      battery: -4.999345555555555,
      ev: 0.0,
      grid: -0.8438511111111111,
      house: 0.36695555555555553,
      soc: 55.14126666666667,
      solar: 6.210152222222222,
      timestamp: "2025-02-07T11:00:00+11:00",
    },
    {
      battery: -4.999382222222223,
      ev: 0.0,
      grid: -1.1937127777777778,
      house: 0.37792444444444445,
      soc: 71.18509999999999,
      solar: 6.5710194444444445,
      timestamp: "2025-02-07T11:30:00+11:00",
    },
    {
      battery: -4.997688888888889,
      ev: 0.0,
      grid: -1.41171,
      house: 0.3919033333333333,
      soc: 87.13608333333333,
      solar: 6.801302222222223,
      timestamp: "2025-02-07T12:00:00+11:00",
    },
    {
      battery: -0.9206433333333334,
      ev: 0.0,
      grid: -4.161996666666667,
      house: 0.34477722222222223,
      soc: 96.64991666666667,
      solar: 5.427417222222222,
      timestamp: "2025-02-07T12:30:00+11:00",
    },
    {
      battery: -0.010177777777777779,
      ev: 0.0,
      grid: -4.770802222222223,
      house: 0.2938105555555556,
      soc: 96.72608333333334,
      solar: 5.0747905555555555,
      timestamp: "2025-02-07T13:00:00+11:00",
    },
    {
      battery: -0.010798888888888889,
      ev: 0.0,
      grid: -4.7432477777777775,
      house: 0.3119761111111111,
      soc: 96.76204999999999,
      solar: 5.0660227777777775,
      timestamp: "2025-02-07T13:30:00+11:00",
    },
    {
      battery: -0.010785555555555555,
      ev: 0.0,
      grid: -4.4539,
      house: 0.43583333333333335,
      soc: 96.79845,
      solar: 4.900518888888889,
      timestamp: "2025-02-07T14:00:00+11:00",
    },
    {
      battery: -0.011081111111111111,
      ev: 0.0,
      grid: -3.719801111111111,
      house: 0.4504588888888889,
      soc: 96.83624999999999,
      solar: 4.181341111111111,
      timestamp: "2025-02-07T14:30:00+11:00",
    },
    {
      battery: 0.3531838888888889,
      ev: 0.0,
      grid: -1.1548838888888888,
      house: 2.452593333333333,
      soc: 96.36348333333332,
      solar: 3.2542933333333335,
      timestamp: "2025-02-07T15:00:00+11:00",
    },
    {
      battery: 0.045832222222222226,
      ev: 0.0,
      grid: -0.15225666666666668,
      house: 2.59842,
      soc: 95.69118333333334,
      solar: 2.7048444444444444,
      timestamp: "2025-02-07T15:30:00+11:00",
    },
    {
      battery: 1.9172005555555556,
      ev: 0.0,
      grid: 0.010324444444444445,
      house: 3.3130255555555554,
      soc: 93.01870000000001,
      solar: 1.3855005555555555,
      timestamp: "2025-02-07T16:00:00+11:00",
    },
    {
      battery: 1.5929344444444444,
      ev: 0.0,
      grid: -0.002611111111111111,
      house: 2.202425,
      soc: 85.7608,
      solar: 0.6121016666666667,
      timestamp: "2025-02-07T16:30:00+11:00",
    },
    {
      battery: 1.2067427777777777,
      ev: 0.0,
      grid: 0.0023766666666666667,
      house: 1.708675,
      soc: 81.76823333333333,
      solar: 0.49955555555555553,
      timestamp: "2025-02-07T17:00:00+11:00",
    },
    {
      battery: 1.4565877777777778,
      ev: 0.0,
      grid: 0.002648888888888889,
      house: 1.8497444444444444,
      soc: 77.44353333333333,
      solar: 0.3905077777777778,
      timestamp: "2025-02-07T17:30:00+11:00",
    },
    {
      battery: 1.6698566666666668,
      ev: 0.0,
      grid: -0.0015155555555555556,
      house: 1.9751005555555556,
      soc: 72.10045,
      solar: 0.3067594444444444,
      timestamp: "2025-02-07T18:00:00+11:00",
    },
    {
      battery: 4.709821111111111,
      ev: 0.0,
      grid: -2.8392644444444444,
      house: 2.0970833333333334,
      soc: 60.73391666666668,
      solar: 0.22652666666666665,
      timestamp: "2025-02-07T18:30:00+11:00",
    },
    {
      battery: 2.803366111111111,
      ev: 0.0,
      grid: -1.1034827777777778,
      house: 1.8403072222222223,
      soc: 49.175450000000005,
      solar: 0.14042388888888888,
      timestamp: "2025-02-07T19:00:00+11:00",
    },
    {
      battery: 4.896141666666667,
      ev: 0.0,
      grid: -4.023248333333333,
      house: 0.9005961111111112,
      soc: 34.9316,
      solar: 0.027702777777777778,
      timestamp: "2025-02-07T19:30:00+11:00",
    },
    {
      battery: 0.9510566666666667,
      ev: 0.0,
      grid: -0.07752277777777777,
      house: 0.8735338888888889,
      soc: 26.05786666666667,
      solar: 0.0,
      timestamp: "2025-02-07T20:00:00+11:00",
    },
    {
      battery: 0.7732894444444445,
      ev: 0.0,
      grid: 0.0019094444444444445,
      house: 0.7751988888888889,
      soc: 23.29518333333333,
      solar: 0.0,
      timestamp: "2025-02-07T20:30:00+11:00",
    },
    {
      battery: 0.19547277777777777,
      ev: 0.0,
      grid: 0.5345194444444444,
      house: 0.7299922222222223,
      soc: 21.51495,
      solar: 0.0,
      timestamp: "2025-02-07T21:00:00+11:00",
    },
    {
      battery: 0.2204611111111111,
      ev: 0.0,
      grid: 0.5363922222222223,
      house: 0.7568533333333334,
      soc: 21.06495,
      solar: 0.0,
      timestamp: "2025-02-07T21:30:00+11:00",
    },
    {
      battery: 0.49602666666666667,
      ev: 0.0,
      grid: 0.2629505555555556,
      house: 0.7589772222222222,
      soc: 19.912366666666667,
      solar: 0.0,
      timestamp: "2025-02-07T22:00:00+11:00",
    },
    {
      battery: 0.7840083333333333,
      ev: 0.0,
      grid: 0.0033183333333333333,
      house: 0.7873266666666666,
      soc: 17.613516666666666,
      solar: 0.0,
      timestamp: "2025-02-07T22:30:00+11:00",
    },
    {
      battery: 0.7152272222222222,
      ev: 0.0,
      grid: -0.002642222222222222,
      house: 0.712585,
      soc: 14.981100000000003,
      solar: 0.0,
      timestamp: "2025-02-07T23:00:00+11:00",
    },
    {
      battery: 0.3774572222222222,
      ev: 0.0,
      grid: 0.1453588888888889,
      house: 0.5228161111111111,
      soc: 12.8774,
      solar: 0.0,
      timestamp: "2025-02-07T23:30:00+11:00",
    },
    {
      battery: 0.3847583333333333,
      ev: 0.0,
      grid: 0.008590555555555556,
      house: 0.3933488888888889,
      soc: 11.884750000000002,
      solar: 0.0,
      timestamp: "2025-02-08T00:00:00+11:00",
    },
    {
      battery: 0.29312555555555553,
      ev: 0.0,
      grid: -0.0010288888888888889,
      house: 0.29209666666666667,
      soc: 10.801416666666666,
      solar: 0.0,
      timestamp: "2025-02-08T00:30:00+11:00",
    },
    {
      battery: 0.013524444444444445,
      ev: 0.0,
      grid: 0.2686527777777778,
      house: 0.28217722222222225,
      soc: 3.0157749999999997,
      solar: 0.0,
      timestamp: "2025-02-08T01:00:00+11:00",
    },
    {
      battery: -0.00989,
      ev: 0.0,
      grid: 0.27750722222222224,
      house: 0.26761722222222223,
      soc: 3.0538066666666666,
      solar: 0.0,
      timestamp: "2025-02-08T01:30:00+11:00",
    },
    {
      battery: -0.009488333333333333,
      ev: 0.0,
      grid: 0.30726,
      house: 0.29777166666666666,
      soc: 3.0899900000000002,
      solar: 0.0,
      timestamp: "2025-02-08T02:00:00+11:00",
    },
    {
      battery: -0.009132222222222222,
      ev: 0.0,
      grid: 0.2800838888888889,
      house: 0.27095166666666665,
      soc: 3.1250816666666665,
      solar: 0.0,
      timestamp: "2025-02-08T02:30:00+11:00",
    },
    {
      battery: -0.008956666666666667,
      ev: 0.0,
      grid: 0.2886827777777778,
      house: 0.2797261111111111,
      soc: 3.1590966666666667,
      solar: 0.0,
      timestamp: "2025-02-08T03:00:00+11:00",
    },
    {
      battery: -0.008857222222222223,
      ev: 0.0,
      grid: 0.25994222222222224,
      house: 0.251085,
      soc: 3.1927533333333336,
      solar: 0.0,
      timestamp: "2025-02-08T03:30:00+11:00",
    },
    {
      battery: -0.008728888888888888,
      ev: 0.0,
      grid: 0.2215111111111111,
      house: 0.21278222222222223,
      soc: 3.225918333333334,
      solar: 0.0,
      timestamp: "2025-02-08T04:00:00+11:00",
    },
    {
      battery: -0.008694444444444444,
      ev: 0.0,
      grid: 0.27487388888888886,
      house: 0.26617944444444447,
      soc: 3.258696666666667,
      solar: 0.0,
      timestamp: "2025-02-08T04:30:00+11:00",
    },
    {
      battery: -0.008643888888888888,
      ev: 0.0,
      grid: 0.25664944444444443,
      house: 0.24800555555555556,
      soc: 3.2917533333333338,
      solar: 0.0,
      timestamp: "2025-02-08T05:00:00+11:00",
    },
    {
      battery: -0.008602777777777779,
      ev: 0.0,
      grid: 0.2369938888888889,
      house: 0.2283911111111111,
      soc: 3.324085,
      solar: 0.0,
      timestamp: "2025-02-08T05:30:00+11:00",
    },
    {
      battery: -0.00845111111111111,
      ev: 0.0,
      grid: 0.3005788888888889,
      house: 0.29460444444444445,
      soc: 3.3563833333333335,
      solar: 0.0024766666666666665,
      timestamp: "2025-02-08T06:00:00+11:00",
    },
    {
      battery: -0.008278333333333334,
      ev: 0.0,
      grid: 0.23647333333333334,
      house: 0.3637011111111111,
      soc: 3.3875300000000004,
      solar: 0.1355061111111111,
      timestamp: "2025-02-08T06:30:00+11:00",
    },
    {
      battery: -0.007816666666666666,
      ev: 0.0,
      grid: 0.4656077777777778,
      house: 0.7202522222222222,
      soc: 3.417571666666667,
      solar: 0.2624611111111111,
      timestamp: "2025-02-08T07:00:00+11:00",
    },
    {
      battery: -0.03611611111111111,
      ev: 0.0,
      grid: 0.00441,
      house: 0.6358411111111111,
      soc: 3.463741666666667,
      solar: 0.6675472222222222,
      timestamp: "2025-02-08T07:30:00+11:00",
    },
    {
      battery: -0.0995888888888889,
      ev: 0.0,
      grid: 0.07157888888888889,
      house: 0.6489383333333333,
      soc: 3.826075,
      solar: 0.6769483333333334,
      timestamp: "2025-02-08T08:00:00+11:00",
    },
    {
      battery: -0.5077588888888889,
      ev: 0.0,
      grid: 0.004996666666666667,
      house: 0.6554044444444445,
      soc: 4.78098,
      solar: 1.1581666666666666,
      timestamp: "2025-02-08T08:30:00+11:00",
    },
    {
      battery: -1.1132277777777777,
      ev: 0.0,
      grid: -0.007826666666666666,
      house: 0.6363588888888889,
      soc: 7.445918333333334,
      solar: 1.7574133333333333,
      timestamp: "2025-02-08T09:00:00+11:00",
    },
    {
      battery: -0.6941216666666666,
      ev: 0.0,
      grid: -0.6061644444444444,
      house: 2.641353888888889,
      soc: 11.642499999999998,
      solar: 3.94164,
      timestamp: "2025-02-08T09:30:00+11:00",
    },
    {
      battery: -0.011280555555555556,
      ev: 0.0,
      grid: -3.289997777777778,
      house: 1.6215177777777778,
      soc: 11.642299999999999,
      solar: 4.922796111111111,
      timestamp: "2025-02-08T10:00:00+11:00",
    },
    {
      battery: -0.011069444444444444,
      ev: 0.0,
      grid: -2.2842944444444444,
      house: 1.718385,
      soc: 11.681049999999999,
      solar: 4.013748888888889,
      timestamp: "2025-02-08T10:30:00+11:00",
    },
    {
      battery: -4.295797222222222,
      ev: 0.0,
      grid: -0.19745166666666666,
      house: 1.5709005555555555,
      soc: 19.63638333333333,
      solar: 6.064149444444444,
      timestamp: "2025-02-08T11:00:00+11:00",
    },
    {
      battery: -3.9437088888888887,
      ev: 0.0,
      grid: -0.4330111111111111,
      house: 1.5715894444444445,
      soc: 32.88741666666666,
      solar: 5.948309444444445,
      timestamp: "2025-02-08T11:30:00+11:00",
    },
    {
      battery: -4.51055,
      ev: 0.0,
      grid: -0.45264055555555555,
      house: 1.5623194444444444,
      soc: 46.53391666666667,
      solar: 6.52551,
      timestamp: "2025-02-08T12:00:00+11:00",
    },
    {
      battery: -2.362693888888889,
      ev: 0.0,
      grid: -1.2673716666666666,
      house: 1.6029977777777777,
      soc: 58.01851666666666,
      solar: 5.233063333333333,
      timestamp: "2025-02-08T12:30:00+11:00",
    },
    {
      battery: -4.640317222222222,
      ev: 0.0,
      grid: -0.7288805555555555,
      house: 1.4759422222222223,
      soc: 69.21346666666666,
      solar: 6.84514,
      timestamp: "2025-02-08T13:00:00+11:00",
    },
    {
      battery: -1.6174105555555556,
      ev: 0.0,
      grid: -1.069406111111111,
      house: 1.4561155555555556,
      soc: 79.60791666666667,
      solar: 4.142932222222222,
      timestamp: "2025-02-08T13:30:00+11:00",
    },
    {
      battery: -2.297956666666667,
      ev: 0.0,
      grid: 0.05481777777777778,
      house: 2.0304355555555555,
      soc: 85.2397,
      solar: 4.273574444444445,
      timestamp: "2025-02-08T14:00:00+11:00",
    },
    {
      battery: -1.9127077777777777,
      ev: 0.0,
      grid: -0.12388388888888889,
      house: 1.7228916666666667,
      soc: 91.93021666666668,
      solar: 3.7594833333333333,
      timestamp: "2025-02-08T14:30:00+11:00",
    },
    {
      battery: 0.28323444444444446,
      ev: 0.0,
      grid: -1.7109522222222222,
      house: 1.9779205555555555,
      soc: 93.38155,
      solar: 3.4056383333333335,
      timestamp: "2025-02-08T15:00:00+11:00",
    },
    {
      battery: 1.7076822222222223,
      ev: 0.0,
      grid: -1.6778422222222222,
      house: 2.071282777777778,
      soc: 90.67596666666667,
      solar: 2.0414427777777777,
      timestamp: "2025-02-08T15:30:00+11:00",
    },
    {
      battery: 1.1548983333333334,
      ev: 0.0,
      grid: -0.007582777777777778,
      house: 1.8520833333333333,
      soc: 84.82970000000002,
      solar: 0.7047677777777778,
      timestamp: "2025-02-08T16:00:00+11:00",
    },
    {
      battery: 4.448946111111111,
      ev: 0.0,
      grid: -3.142002222222222,
      house: 1.76439,
      soc: 75.36788333333332,
      solar: 0.4574461111111111,
      timestamp: "2025-02-08T16:30:00+11:00",
    },
    {
      battery: 1.3732533333333334,
      ev: 0.0,
      grid: -0.06899055555555555,
      house: 1.676471111111111,
      soc: 66.3243,
      solar: 0.3722083333333333,
      timestamp: "2025-02-08T17:00:00+11:00",
    },
    {
      battery: 1.1533127777777779,
      ev: 0.0,
      grid: 0.0008861111111111111,
      house: 1.4557883333333332,
      soc: 62.01453333333333,
      solar: 0.30158944444444447,
      timestamp: "2025-02-08T17:30:00+11:00",
    },
    {
      battery: 0.43467833333333333,
      ev: 0.0,
      grid: 0.005024444444444444,
      house: 0.6379444444444444,
      soc: 59.73023333333333,
      solar: 0.19824166666666668,
      timestamp: "2025-02-08T18:00:00+11:00",
    },
    {
      battery: 0.7228483333333333,
      ev: 0.0,
      grid: -0.002448333333333333,
      house: 0.9433016666666667,
      soc: 57.64798333333332,
      solar: 0.22290166666666666,
      timestamp: "2025-02-08T18:30:00+11:00",
    },
    {
      battery: 2.165365,
      ev: 0.0,
      grid: -1.162741111111111,
      house: 1.0703666666666667,
      soc: 52.39483333333334,
      solar: 0.06774277777777778,
      timestamp: "2025-02-08T19:00:00+11:00",
    },
    {
      battery: 1.1235022222222222,
      ev: 0.0,
      grid: 0.0009933333333333333,
      house: 1.1257516666666667,
      soc: 47.41483333333333,
      solar: 0.001256111111111111,
      timestamp: "2025-02-08T19:30:00+11:00",
    },
    {
      battery: 0.7319666666666667,
      ev: 0.0,
      grid: 0.0011766666666666666,
      house: 0.7331433333333334,
      soc: 44.41675,
      solar: 0.0,
      timestamp: "2025-02-08T20:00:00+11:00",
    },
    {
      battery: 0.7533005555555555,
      ev: 0.0,
      grid: 0.0014633333333333334,
      house: 0.7547638888888889,
      soc: 41.93731666666667,
      solar: 0.0,
      timestamp: "2025-02-08T20:30:00+11:00",
    },
    {
      battery: 0.8189566666666667,
      ev: 0.0,
      grid: 0.0014022222222222222,
      house: 0.8203588888888889,
      soc: 39.3616,
      solar: 0.0,
      timestamp: "2025-02-08T21:00:00+11:00",
    },
    {
      battery: 0.776185,
      ev: 0.0,
      grid: 0.0012372222222222222,
      house: 0.7774222222222222,
      soc: 36.61848333333334,
      solar: 0.0,
      timestamp: "2025-02-08T21:30:00+11:00",
    },
    {
      battery: 0.45609833333333333,
      ev: 0.0,
      grid: 0.004331111111111111,
      house: 0.46042944444444445,
      soc: 34.83153333333333,
      solar: 0.0,
      timestamp: "2025-02-08T22:00:00+11:00",
    },
    {
      battery: 0.6110772222222223,
      ev: 0.0,
      grid: -0.00010833333333333333,
      house: 0.6109688888888889,
      soc: 32.93945,
      solar: 0.0,
      timestamp: "2025-02-08T22:30:00+11:00",
    },
    {
      battery: 0.5801261111111111,
      ev: 0.0,
      grid: 0.0013927777777777777,
      house: 0.5815188888888889,
      soc: 30.901183333333332,
      solar: 0.0,
      timestamp: "2025-02-08T23:00:00+11:00",
    },
    {
      battery: 1.0862538888888889,
      ev: 0.0,
      grid: 0.0025677777777777778,
      house: 1.0888216666666666,
      soc: 28.189133333333334,
      solar: 0.0,
      timestamp: "2025-02-08T23:30:00+11:00",
    },
    {
      battery: 1.0519344444444445,
      ev: 0.0,
      grid: 0.0026755555555555556,
      house: 1.05461,
      soc: 24.36725,
      solar: 0.0,
      timestamp: "2025-02-09T00:00:00+11:00",
    },
    {
      battery: 0.79166,
      ev: 0.0,
      grid: 0.0012838888888888889,
      house: 0.7929438888888889,
      soc: 21.360716666666665,
      solar: 0.0,
      timestamp: "2025-02-09T00:30:00+11:00",
    },
    {
      battery: 0.06815277777777778,
      ev: 0.0,
      grid: 0.0996761111111111,
      house: 0.1678288888888889,
      soc: 20.05455,
      solar: 0.0,
      timestamp: "2025-02-09T01:00:00+11:00",
    },
    {
      battery: 0.16029333333333334,
      ev: 0.0,
      grid: 0.05825888888888889,
      house: 0.21855222222222223,
      soc: 19.623583333333332,
      solar: 0.0,
      timestamp: "2025-02-09T01:30:00+11:00",
    },
    {
      battery: 0.11813055555555556,
      ev: 0.0,
      grid: 0.07440611111111112,
      house: 0.19253666666666666,
      soc: 19.2942,
      solar: 0.0,
      timestamp: "2025-02-09T02:00:00+11:00",
    },
    {
      battery: 0.12846444444444444,
      ev: 0.0,
      grid: 0.07773833333333334,
      house: 0.2062027777777778,
      soc: 18.98271666666667,
      solar: 0.0,
      timestamp: "2025-02-09T02:30:00+11:00",
    },
    {
      battery: 0.21864166666666668,
      ev: 0.0,
      grid: 0.044455555555555554,
      house: 0.2630972222222222,
      soc: 18.35075,
      solar: 0.0,
      timestamp: "2025-02-09T03:00:00+11:00",
    },
    {
      battery: 0.20334833333333333,
      ev: 0.0,
      grid: 0.051705,
      house: 0.25505333333333335,
      soc: 17.438533333333336,
      solar: 0.0,
      timestamp: "2025-02-09T03:30:00+11:00",
    },
    {
      battery: 0.27033666666666667,
      ev: 0.0,
      grid: 0.004807777777777778,
      house: 0.27514444444444447,
      soc: 16.700550000000003,
      solar: 0.0,
      timestamp: "2025-02-09T04:00:00+11:00",
    },
    {
      battery: 0.19614666666666666,
      ev: 0.0,
      grid: 0.041062777777777774,
      house: 0.23720944444444444,
      soc: 16.004433333333335,
      solar: 0.0,
      timestamp: "2025-02-09T04:30:00+11:00",
    },
    {
      battery: 0.5054033333333333,
      ev: 0.0,
      grid: -0.25469,
      house: 0.25071333333333334,
      soc: 14.888066666666667,
      solar: 0.0,
      timestamp: "2025-02-09T05:00:00+11:00",
    },
    {
      battery: 0.16568888888888889,
      ev: 0.0,
      grid: 0.043658333333333334,
      house: 0.2093472222222222,
      soc: 13.585616666666668,
      solar: 0.0,
      timestamp: "2025-02-09T05:30:00+11:00",
    },
    {
      battery: 0.46301,
      ev: 0.0,
      grid: -0.22477444444444444,
      house: 0.2382438888888889,
      soc: 12.44075,
      solar: 8.333333333333334e-6,
      timestamp: "2025-02-09T06:00:00+11:00",
    },
    {
      battery: 0.2554766666666667,
      ev: 0.0,
      grid: -0.12173611111111111,
      house: 0.21508388888888888,
      soc: 11.225516666666666,
      solar: 0.08134333333333334,
      timestamp: "2025-02-09T06:30:00+11:00",
    },
    {
      battery: 0.2408611111111111,
      ev: 0.0,
      grid: -0.006309444444444445,
      house: 0.5408488888888889,
      soc: 10.414733333333333,
      solar: 0.3062972222222222,
      timestamp: "2025-02-09T07:00:00+11:00",
    },
    {
      battery: -0.21102166666666666,
      ev: 0.0,
      grid: -0.014839444444444445,
      house: 0.2263711111111111,
      soc: 10.44385,
      solar: 0.4522322222222222,
      timestamp: "2025-02-09T07:30:00+11:00",
    },
    {
      battery: -0.46107166666666666,
      ev: 0.0,
      grid: -0.0038544444444444446,
      house: 0.30075,
      soc: 11.578416666666667,
      solar: 0.7656761111111111,
      timestamp: "2025-02-09T08:00:00+11:00",
    },
    {
      battery: -1.0392805555555555,
      ev: 0.0,
      grid: -0.0033483333333333334,
      house: 0.29168055555555555,
      soc: 14.106216666666667,
      solar: 1.3343094444444445,
      timestamp: "2025-02-09T08:30:00+11:00",
    },
    {
      battery: -1.828785,
      ev: 3.8,
      grid: -0.0004005555555555556,
      house: 0.3265544444444444,
      soc: 19.23905,
      solar: 2.15574,
      timestamp: "2025-02-09T09:00:00+11:00",
    },
    {
      battery: -0.58394,
      ev: 3.8,
      grid: -1.5277933333333333,
      house: 0.5146627777777778,
      soc: 22.725783333333336,
      solar: 2.626396111111111,
      timestamp: "2025-02-09T09:30:00+11:00",
    },
    {
      battery: -0.7358988888888889,
      ev: 3.8,
      grid: 0.11637611111111111,
      house: 1.5281888888888888,
      soc: 25.8383,
      solar: 2.1477116666666665,
      timestamp: "2025-02-09T10:00:00+11:00",
    },
    {
      battery: -1.278061111111111,
      ev: 3.8,
      grid: -0.09228277777777778,
      house: 1.6009977777777777,
      soc: 26.99495,
      solar: 2.9713416666666665,
      timestamp: "2025-02-09T10:30:00+11:00",
    },
    {
      battery: -2.918085,
      ev: 3.8,
      grid: -0.20395777777777777,
      house: 1.734513888888889,
      soc: 35.94401666666667,
      solar: 4.856556666666667,
      timestamp: "2025-02-09T11:00:00+11:00",
    },
    {
      battery: -3.06112,
      ev: 3.8,
      grid: -0.08069666666666667,
      house: 2.2138155555555556,
      soc: 45.9244,
      solar: 5.355632222222222,
      timestamp: "2025-02-09T11:30:00+11:00",
    },
    {
      battery: -1.347845,
      ev: 3.8,
      grid: 0.32955222222222225,
      house: 2.2515705555555554,
      soc: 53.426216666666676,
      solar: 3.2698633333333333,
      timestamp: "2025-02-09T12:00:00+11:00",
    },
    {
      battery: -2.9790566666666667,
      ev: 4.8,
      grid: -0.07905166666666667,
      house: 2.5774705555555557,
      soc: 59.096383333333335,
      solar: 5.6355788888888885,
      timestamp: "2025-02-09T12:30:00+11:00",
    },
    {
      battery: -4.872005,
      ev: 4.8,
      grid: -0.6337883333333333,
      house: 1.8559016666666666,
      soc: 72.41628333333334,
      solar: 7.361695,
      timestamp: "2025-02-09T13:00:00+11:00",
    },
    {
      battery: -2.1670088888888888,
      ev: 4.8,
      grid: -0.059972222222222225,
      house: 2.0544272222222224,
      soc: 83.46526666666666,
      solar: 4.281408333333333,
      timestamp: "2025-02-09T13:30:00+11:00",
    },
    {
      battery: -1.6534744444444445,
      ev: 4.8,
      grid: 1.2392144444444444,
      house: 1.9520411111111111,
      soc: 87.75900000000001,
      solar: 2.366301111111111,
      timestamp: "2025-02-09T14:00:00+11:00",
    },
    {
      battery: -1.8665883333333333,
      ev: 4.8,
      grid: 0.11650944444444444,
      house: 2.0908405555555554,
      soc: 94.97148333333332,
      solar: 3.8409194444444443,
      timestamp: "2025-02-09T14:30:00+11:00",
    },
    {
      battery: 0.028934444444444443,
      ev: 4.8,
      grid: -0.27517444444444444,
      house: 2.0892144444444445,
      soc: 96.84989999999999,
      solar: 2.3354544444444443,
      timestamp: "2025-02-09T15:00:00+11:00",
    },
    {
      battery: 0.9305172222222222,
      ev: 4.8,
      grid: 0.0002572222222222222,
      house: 2.7752616666666667,
      soc: 95.1708,
      solar: 1.8444872222222222,
      timestamp: "2025-02-09T15:30:00+11:00",
    },
    {
      battery: -0.7391694444444444,
      ev: 4.8,
      grid: -0.0063638888888888885,
      house: 1.2435272222222222,
      soc: 94.6737,
      solar: 1.9890605555555556,
      timestamp: "2025-02-09T16:00:00+11:00",
    },
    {
      battery: -0.6735677777777778,
      ev: 4.8,
      grid: -0.22164555555555557,
      house: 0.48892833333333335,
      soc: 97.63313333333333,
      solar: 1.3841416666666666,
      timestamp: "2025-02-09T16:30:00+11:00",
    },
    {
      battery: -0.3878827777777778,
      ev: 4.8,
      grid: -0.02652111111111111,
      house: 0.4538088888888889,
      soc: 99.52451666666667,
      solar: 0.8682127777777778,
      timestamp: "2025-02-09T17:00:00+11:00",
    },
    {
      battery: 0.5324961111111111,
      ev: 6.5,
      grid: -0.6891944444444444,
      house: 0.4080827777777778,
      soc: 98.96078333333332,
      solar: 0.5647811111111111,
      timestamp: "2025-02-09T17:30:00+11:00",
    },
    {
      battery: 0.1736588888888889,
      ev: 6.5,
      grid: 0.004251111111111111,
      house: 0.3976677777777778,
      soc: 97.52808333333333,
      solar: 0.21975777777777777,
      timestamp: "2025-02-09T18:00:00+11:00",
    },
    {
      battery: 0.3023266666666667,
      ev: 6.5,
      grid: 0.002903888888888889,
      house: 0.3836888888888889,
      soc: 96.68091666666668,
      solar: 0.07845833333333334,
      timestamp: "2025-02-09T18:30:00+11:00",
    },
    {
      battery: 0.34911,
      ev: 6.5,
      grid: 0.003655,
      house: 0.3712538888888889,
      soc: 95.63101666666667,
      solar: 0.018488888888888888,
      timestamp: "2025-02-09T19:00:00+11:00",
    },
    {
      battery: 2.705286111111111,
      ev: 6.5,
      grid: -1.1859883333333334,
      house: 1.5195838888888888,
      soc: 91.79381666666667,
      solar: 0.0002861111111111111,
      timestamp: "2025-02-09T19:30:00+11:00",
    },
    {
      battery: 2.1605544444444447,
      ev: 6.5,
      grid: -0.10431944444444445,
      house: 2.056235,
      soc: 81.8209,
      solar: 0.0,
      timestamp: "2025-02-09T20:00:00+11:00",
    },
    {
      battery: 1.8577605555555556,
      ev: 6.5,
      grid: 0.001068888888888889,
      house: 1.8588294444444444,
      soc: 75.45023333333332,
      solar: 0.0,
      timestamp: "2025-02-09T20:30:00+11:00",
    },
    {
      battery: 1.69033,
      ev: 0.0,
      grid: 0.0018761111111111112,
      house: 1.692206111111111,
      soc: 69.69731666666667,
      solar: 0.0,
      timestamp: "2025-02-09T21:00:00+11:00",
    },
    {
      battery: 1.4261244444444445,
      ev: 0.0,
      grid: 0.0005783333333333334,
      house: 1.4267027777777779,
      soc: 64.52875,
      solar: 0.0,
      timestamp: "2025-02-09T21:30:00+11:00",
    },
    {
      battery: 1.0819633333333334,
      ev: 0.0,
      grid: 0.0007472222222222223,
      house: 1.0827105555555556,
      soc: 60.29258333333334,
      solar: 0.0,
      timestamp: "2025-02-09T22:00:00+11:00",
    },
    {
      battery: 0.6413422222222223,
      ev: 0.0,
      grid: 0.0024916666666666668,
      house: 0.6438338888888889,
      soc: 57.77741666666666,
      solar: 0.0,
      timestamp: "2025-02-09T22:30:00+11:00",
    },
    {
      battery: 0.5812033333333333,
      ev: 0.0,
      grid: 0.0019455555555555556,
      house: 0.5831488888888889,
      soc: 55.76975000000001,
      solar: 0.0,
      timestamp: "2025-02-09T23:00:00+11:00",
    },
    {
      battery: 0.467685,
      ev: 0.0,
      grid: 0.0016461111111111112,
      house: 0.4693311111111111,
      soc: 54.13021666666666,
      solar: 0.0,
      timestamp: "2025-02-09T23:30:00+11:00",
    },
  ];
};

const getMonthData = () => {
  return [
    {
      solar: 27.296349444444445,
      ev: 0.0,
      grid_import: 15.807396944444445,
      grid_export: -2.6651438888888888,
      house: 38.745575,
      battery_import: 13.9686975,
      battery_export: -12.27567,
      soc: 96.3774,
      timestamp: "2025-03-01T00:00:00+11:00",
    },
    {
      solar: 24.642339444444445,
      ev: 0.0,
      grid_import: 16.65570611111111,
      grid_export: -1.8040886111111112,
      house: 32.246227777777776,
      battery_import: 14.146206666666666,
      battery_export: -6.8984775,
      soc: 100.0,
      timestamp: "2025-03-02T00:00:00+11:00",
    },
    {
      solar: 20.52581111111111,
      ev: 0.0,
      grid_import: 7.892408055555555,
      grid_export: -7.147818055555556,
      house: 27.705778055555555,
      battery_import: 5.84754,
      battery_export: -12.282916944444445,
      soc: 74.2216,
      timestamp: "2025-03-03T00:00:00+11:00",
    },
    {
      solar: 30.560684722222224,
      ev: 0.0,
      grid_import: 19.16240361111111,
      grid_export: -18.432167777777778,
      house: 31.774404166666667,
      battery_import: 13.898664444444444,
      battery_export: -14.382148055555556,
      soc: 100.0,
      timestamp: "2025-03-04T00:00:00+11:00",
    },
    {
      solar: 29.995413055555556,
      ev: 0.0,
      grid_import: 14.754899166666666,
      grid_export: -12.354449444444445,
      house: 31.618503055555557,
      battery_import: 14.638763333333333,
      battery_export: -13.861403611111111,
      soc: 100.0,
      timestamp: "2025-03-05T00:00:00+11:00",
    },
    {
      solar: 26.453544166666667,
      ev: 0.0,
      grid_import: 11.12325388888889,
      grid_export: -9.416355277777777,
      house: 25.897470555555557,
      battery_import: 14.357276666666667,
      battery_export: -12.094304444444445,
      soc: 100.0,
      timestamp: "2025-03-06T00:00:00+11:00",
    },
    {
      solar: 17.465249444444446,
      ev: 0.0,
      grid_import: 20.089636944444443,
      grid_export: -10.799095,
      house: 26.4321425,
      battery_import: 13.935695555555556,
      battery_export: -13.612046666666666,
      soc: 100.0,
      timestamp: "2025-03-07T00:00:00+11:00",
    },
    {
      solar: 20.842758055555556,
      ev: 0.0,
      grid_import: 25.102920555555556,
      grid_export: -3.110555277777778,
      house: 34.217531666666666,
      battery_import: 14.31241,
      battery_export: -5.694818333333333,
      soc: 100.0,
      timestamp: "2025-03-08T00:00:00+11:00",
    },
    {
      solar: 29.631148333333332,
      ev: 0.0,
      grid_import: 10.437496388888889,
      grid_export: -3.216665,
      house: 38.15348,
      battery_import: 7.8133936111111115,
      battery_export: -9.114893888888888,
      soc: 100.0,
      timestamp: "2025-03-09T00:00:00+11:00",
    },
    {
      solar: 21.399705833333332,
      ev: 0.0,
      grid_import: 11.707917777777778,
      grid_export: -2.3634263888888887,
      house: 30.37306277777778,
      battery_import: 6.709611944444444,
      battery_export: -6.3384775,
      soc: 100.0,
      timestamp: "2025-03-10T00:00:00+11:00",
    },
    {
      solar: 13.31309861111111,
      ev: 0.0,
      grid_import: 16.758577777777777,
      grid_export: -2.7525077777777778,
      house: 31.685758333333332,
      battery_import: 6.167790277777778,
      battery_export: -10.53438,
      soc: 62.0193,
      timestamp: "2025-03-11T00:00:00+11:00",
    },
    {
      solar: 30.78921777777778,
      ev: 0.0,
      grid_import: 12.29916388888889,
      grid_export: -5.090458611111111,
      house: 40.20852055555556,
      battery_import: 10.693987777777778,
      battery_export: -12.904585277777779,
      soc: 77.8127,
      timestamp: "2025-03-12T00:00:00+11:00",
    },
    {
      solar: 33.744074166666664,
      ev: 0.0,
      grid_import: 12.815643055555556,
      grid_export: -6.176461388888889,
      house: 40.27229333333333,
      battery_import: 12.651719166666666,
      battery_export: -12.540756666666667,
      soc: 91.6021,
      timestamp: "2025-03-13T00:00:00+11:00",
    },
    {
      solar: 32.70083555555556,
      ev: 0.0,
      grid_import: 16.87768611111111,
      grid_export: -1.5423966666666666,
      house: 47.93664138888889,
      battery_import: 10.174896111111112,
      battery_export: -10.0754125,
      soc: 74.6233,
      timestamp: "2025-03-14T00:00:00+11:00",
    },
    {
      solar: 31.76212472222222,
      ev: 0.0,
      grid_import: 21.852720555555557,
      grid_export: -10.05215861111111,
      house: 40.034295,
      battery_import: 14.635370833333333,
      battery_export: -11.106979166666667,
      soc: 100.0,
      timestamp: "2025-03-15T00:00:00+11:00",
    },
    {
      solar: 25.564151944444443,
      ev: 0.0,
      grid_import: 22.56725888888889,
      grid_export: -0.5623811111111111,
      house: 48.260304166666664,
      battery_import: 14.844458888888889,
      battery_export: -15.535733333333333,
      soc: 100.0,
      timestamp: "2025-03-16T00:00:00+11:00",
    },
    {
      solar: 27.22176138888889,
      ev: 0.0,
      grid_import: 10.299765277777778,
      grid_export: -3.8175855555555556,
      house: 24.85064361111111,
      battery_import: 14.299643055555556,
      battery_export: -5.446345555555555,
      soc: 100.0,
      timestamp: "2025-03-17T00:00:00+11:00",
    },
    {
      solar: 30.19948,
      ev: 0.0,
      grid_import: 2.6112783333333334,
      grid_export: -6.303983611111111,
      house: 26.792649722222222,
      battery_import: 10.037244166666667,
      battery_export: -10.323119166666666,
      soc: 100.0,
      timestamp: "2025-03-18T00:00:00+11:00",
    },
    {
      solar: 29.042311944444446,
      ev: 0.0,
      grid_import: 10.848389166666667,
      grid_export: -17.782809444444446,
      house: 30.668405555555555,
      battery_import: 13.692470277777778,
      battery_export: -22.252984166666668,
      soc: 100.0,
      timestamp: "2025-03-19T00:00:00+11:00",
    },
    {
      solar: 29.762384166666667,
      ev: 0.0,
      grid_import: 9.508502777777778,
      grid_export: -5.627646944444445,
      house: 32.464954444444444,
      battery_import: 15.421512777777778,
      battery_export: -14.243227222222222,
      soc: 100.0,
      timestamp: "2025-03-20T00:00:00+11:00",
    },
    {
      solar: 10.055996944444445,
      ev: 0.0,
      grid_import: 32.620416666666664,
      grid_export: -8.159311666666667,
      house: 33.04513583333333,
      battery_import: 14.992981944444445,
      battery_export: -13.521015833333333,
      soc: 98.8299,
      timestamp: "2025-03-21T00:00:00+11:00",
    },
    {
      solar: 20.408399444444445,
      ev: 0.0,
      grid_import: 31.352876944444443,
      grid_export: -6.563738611111111,
      house: 45.98697666666666,
      battery_import: 16.061730277777777,
      battery_export: -16.851169166666665,
      soc: 99.3768,
      timestamp: "2025-03-22T00:00:00+11:00",
    },
    {
      solar: 14.876542222222222,
      ev: 26.5,
      grid_import: 31.987104722222224,
      grid_export: -0.13232888888888888,
      house: 39.415306111111114,
      battery_import: 12.118563055555555,
      battery_export: -4.802551111111111,
      soc: 77.8455,
      timestamp: "2025-03-23T00:00:00+11:00",
    },
    {
      solar: 18.94005472222222,
      ev: 0.0,
      grid_import: 16.620629166666667,
      grid_export: -3.2092358333333335,
      house: 36.10604583333333,
      battery_import: 8.640045833333334,
      battery_export: -12.39464361111111,
      soc: 61.4825,
      timestamp: "2025-03-24T00:00:00+11:00",
    },
    {
      solar: 12.397888333333333,
      ev: 0.0,
      grid_import: 18.91224888888889,
      grid_export: -1.4791894444444444,
      house: 32.323349444444446,
      battery_import: 8.522676666666667,
      battery_export: -11.015078333333333,
      soc: 55.3907,
      timestamp: "2025-03-25T00:00:00+11:00",
    },
    {
      solar: 25.805583055555555,
      ev: 0.0,
      grid_import: 19.450963055555555,
      grid_export: -0.8273636111111111,
      house: 37.22951472222222,
      battery_import: 16.301465277777776,
      battery_export: -9.1017975,
      soc: 100.0,
      timestamp: "2025-03-26T00:00:00+11:00",
    },
    {
      solar: 18.50682972222222,
      ev: 0.0,
      grid_import: 15.779221944444444,
      grid_export: -0.4143927777777778,
      house: 33.84183083333333,
      battery_import: 13.1334575,
      battery_export: -13.103629444444444,
      soc: 72.2823,
      timestamp: "2025-03-27T00:00:00+11:00",
    },
    {
      solar: 19.14554083333333,
      ev: 0.0,
      grid_import: 18.988440833333332,
      grid_export: -0.6488511111111112,
      house: 32.64491777777778,
      battery_import: 17.6305875,
      battery_export: -12.790374722222221,
      soc: 100.0,
      timestamp: "2025-03-28T00:00:00+11:00",
    },
    {
      solar: 5.253363055555556,
      ev: 0.0,
      grid_import: 19.80337777777778,
      grid_export: -0.060678055555555555,
      house: 28.825149444444445,
      battery_import: 8.941023333333334,
      battery_export: -12.77011,
      soc: 79.3612,
      timestamp: "2025-03-29T00:00:00+11:00",
    },
    {
      solar: 20.34185777777778,
      ev: 0.0,
      grid_import: 24.860060555555556,
      grid_export: -3.857016111111111,
      house: 42.923789444444445,
      battery_import: 16.069366111111112,
      battery_export: -17.648253333333333,
      soc: 100.0,
      timestamp: "2025-03-30T00:00:00+11:00",
    },
    {
      solar: 17.8413925,
      ev: 15.8,
      grid_import: 28.665841944444445,
      grid_export: -0.122765,
      house: 41.837332777777775,
      battery_import: 15.335994722222223,
      battery_export: -10.788858055555556,
      soc: 89.0645,
      timestamp: "2025-03-31T00:00:00+11:00",
    },
  ];
};

const getYearData = () => {
  return [];
};
